/**
 * CounterSalesPage Refactorizado con componentes compartidos
 */
import React, { useState, useEffect, useRef } from 'react';
import { ShoppingCart, Plus, Minus, Search, Trash2, Package, X, User, CreditCard, Layers, Menu, Printer, DollarSign, Banknote, RefreshCw, AlertTriangle, ArrowUpFromLine, RotateCcw, Check, Clock, FileText, Play, Warehouse, Edit3, History } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import Button from '../../components/ui/Button';
import Input from '../../components/forms/Input';
import Modal from '../../components/ui/Modal';
import PricingByQuantity from '../../components/pricing/PricingByQuantity';
import QuantityModal from '../../components/sales/QuantityModal';
import ProductsCatalogModal from '../../components/sales/ProductsCatalogModal';
import apiService from '../../services/apiService';
import { formatMoney } from '../../utils/formatters';
import './NewSaleForm.css';

// Hooks compartidos
import useCart from '../../hooks/useCart';
import usePermissions from '../../hooks/usePermissions';

// Función para verificar si la unidad de medida requiere decimales (kilo/gramo)
const esUnidadPeso = (unidad) => {
    if (!unidad) return false;
    const unidadLower = unidad.toLowerCase().trim();
    return ['kilo', 'kilos', 'kg', 'kilogramo', 'kilogramos', 'gramo', 'gramos', 'g', 'gr'].includes(unidadLower);
};

const CounterSalesPageRefactored = () => {
    const { loading, setLoading, showToast, user, setView } = useApp();
    const { hasPermission } = usePermissions();

    // Configuraciones
    const [configImpresion, setConfigImpresion] = useState({
        impresion_preguntar_ubicacion: true,
        impresion_ubicacion_default: 'local'
    });
    const [comisionTarjeta, setComisionTarjeta] = useState(0);
    const [campoCosto, setCampoCosto] = useState('precio_compra');
    const [unidadesModalCantidad, setUnidadesModalCantidad] = useState([]);
    const [alertaEfectivoMaximo, setAlertaEfectivoMaximo] = useState(0);
    const [empresaLogo, setEmpresaLogo] = useState('');

    // Modal de comanda (selección de ubicación de impresión)
    const [showComandaModal, setShowComandaModal] = useState(false);
    const [ventaPendiente, setVentaPendiente] = useState(null);
    const [impresoraPredeterminada, setImpresoraPredeterminada] = useState('');

    // Menú lateral rápido (Alt)
    const [showQuickMenu, setShowQuickMenu] = useState(false);
    const [totalesCaja, setTotalesCaja] = useState({
        total_efectivo: 0,
        total_efectivo_bruto: 0,
        total_retiros: 0,
        cantidad_retiros: 0,
        total_ingresos: 0,
        cantidad_ingresos: 0,
        total_tarjeta: 0,
        total_transferencia: 0,
        total_ventas: 0,
        cantidad_ventas: 0,
        loading: false
    });

    // Modal de retiro de efectivo (F7)
    const [showRetiroModal, setShowRetiroModal] = useState(false);
    const [retiroData, setRetiroData] = useState({ monto: '', motivo: '' });
    const [savingRetiro, setSavingRetiro] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [passwordRetiro, setPasswordRetiro] = useState('');
    const [verificandoPassword, setVerificandoPassword] = useState(false);
    const [usuarioAutorizador, setUsuarioAutorizador] = useState(null);
    const passwordInputRef = useRef(null);

    // Modal de reimprimir ticket (F8)
    const [showReimprimirModal, setShowReimprimirModal] = useState(false);
    const [ventasParaReimprimir, setVentasParaReimprimir] = useState([]);
    const [loadingVentas, setLoadingVentas] = useState(false);
    const [reimprimiendo, setReimprimiendo] = useState(null);


    // Modal de ingreso de efectivo (F12)
    const [showIngresoModal, setShowIngresoModal] = useState(false);
    const [ingresoData, setIngresoData] = useState({ monto: '', concepto: 'fondo_caja', descripcion: '' });
    const [savingIngreso, setSavingIngreso] = useState(false);
    const [showPasswordIngresoModal, setShowPasswordIngresoModal] = useState(false);
    const [passwordIngreso, setPasswordIngreso] = useState('');
    const [verificandoPasswordIngreso, setVerificandoPasswordIngreso] = useState(false);
    const [usuarioAutorizadorIngreso, setUsuarioAutorizadorIngreso] = useState(null);
    const passwordIngresoInputRef = useRef(null);

    // Modal de precio libre (doble F4)
    const [showPrecioLibreModal, setShowPrecioLibreModal] = useState(false);
    const [precioLibreData, setPrecioLibreData] = useState({ precio: '', itemId: null, precioCosto: 0, productoNombre: '' });
    const [showPasswordPrecioLibreModal, setShowPasswordPrecioLibreModal] = useState(false);
    const [passwordPrecioLibre, setPasswordPrecioLibre] = useState('');
    const [verificandoPasswordPrecioLibre, setVerificandoPasswordPrecioLibre] = useState(false);
    const [usuarioAutorizadorPrecioLibre, setUsuarioAutorizadorPrecioLibre] = useState(null);
    const passwordPrecioLibreInputRef = useRef(null);
    const precioLibreInputRef = useRef(null);
    const lastF4PressRef = useRef(0);

    // Modal de historial producto-cliente (Ctrl+H)
    const [showHistorialModal, setShowHistorialModal] = useState(false);
    const [historialData, setHistorialData] = useState([]);
    const [historialProductoNombre, setHistorialProductoNombre] = useState('');
    const [historialCostoActual, setHistorialCostoActual] = useState(0);
    const [historialCostoTipoActual, setHistorialCostoTipoActual] = useState('');
    const [historialItemId, setHistorialItemId] = useState(null);
    const [loadingHistorial, setLoadingHistorial] = useState(false);

    // Cliente (opcional para mostrador)
    const [cliente, setCliente] = useState(null);
    const [showClientModal, setShowClientModal] = useState(false);
    const [clientSearch, setClientSearch] = useState('');
    const [clientResults, setClientResults] = useState([]);
    const [showClientResults, setShowClientResults] = useState(false);
    const [selectedClientIndex, setSelectedClientIndex] = useState(0);

    // Búsqueda de productos
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [showResults, setShowResults] = useState(false);
    const [selectedSearchResultIndex, setSelectedSearchResultIndex] = useState(0);

    // Hook del carrito compartido
    const {
        cart,
        setCart,
        selectedCartItemId,
        setSelectedCartItemId,
        getCustomerPrice,
        getCustomerPriceFromPresentacion,
        addToCartDirectly,
        addToCartWithQuantity,
        removeFromCart,
        deleteFromCart,
        updateQuantity,
        updateQuantityDebounced,
        changePriceLevel,
        cambiarPresentacion,
        clearCart,
        loadEquivalencias,
        totals
    } = useCart({
        cliente,
        comisionTarjeta,
        showToast
    });

    // Estados de UI
    const [observaciones, setObservaciones] = useState('');
    const [showProductsModal, setShowProductsModal] = useState(false);
    const [showPaymentConfirmModal, setShowPaymentConfirmModal] = useState(false);
    const [pagosDetalle, setPagosDetalle] = useState([]);

    // Modal de cantidad
    const [showQuantityModal, setShowQuantityModal] = useState(null);
    const [equivalenciasDisponibles, setEquivalenciasDisponibles] = useState([]);
    const [loadingEquivalencias, setLoadingEquivalencias] = useState(false);

    // Selector de precios
    const [showPriceSelector, setShowPriceSelector] = useState(null);
    const [selectedPriceIndex, setSelectedPriceIndex] = useState(0);

    // Ventas pausadas
    const [ventasPausadas, setVentasPausadas] = useState(() => {
        const saved = localStorage.getItem('pos_ventas_pausadas');
        return saved ? JSON.parse(saved) : [];
    });
    const [showVentasPausadasModal, setShowVentasPausadasModal] = useState(false);
    const [selectedVentaPausadaIndex, setSelectedVentaPausadaIndex] = useState(0);

    // Refs
    const searchInputRef = useRef(null);
    const searchResultsContainerRef = useRef(null);
    const clientSearchInputRef = useRef(null);

    // Cargar configuraciones
    useEffect(() => {
        const cargarConfiguraciones = async () => {
            try {
                const nombrePc = localStorage.getItem('pos_nombre_pc') || null;
                const [impresionResult, cajaResult, empresaResult, logoResult, syncResult] = await Promise.all([
                    apiService.getConfiguracionesPorCategoria('impresion', nombrePc),
                    apiService.getConfiguracionesPorCategoria('caja', nombrePc),
                    apiService.getConfiguracionesPorCategoria('empresa', null),
                    apiService.getConfiguracion('empresa_logo', null),
                    apiService.getConfiguracionesPorCategoria('sync', null)
                ]);

                if (impresionResult.success && impresionResult.data?.valores) {
                    const impConfig = { ...impresionResult.data.valores };
                    // Agregar sucursal desde sync config
                    if (syncResult.success && syncResult.data?.valores) {
                        impConfig._sucursal = syncResult.data.valores.caja_api_nube_sucursal || '';
                    }
                    setConfigImpresion(impConfig);
                }

                if (cajaResult.success && cajaResult.data?.valores) {
                    const unidadesStr = cajaResult.data.valores.caja_unidades_modal_cantidad || '';
                    const unidades = unidadesStr ? unidadesStr.split(',').map(u => u.trim().toLowerCase()).filter(Boolean) : [];
                    setUnidadesModalCantidad(unidades);
                    const alertaMax = parseFloat(cajaResult.data.valores.caja_alerta_efectivo_maximo) || 0;
                    setAlertaEfectivoMaximo(alertaMax);
                }

                if (empresaResult.success && empresaResult.data?.valores) {
                    const comision = parseFloat(empresaResult.data.valores.empresa_comision_tarjeta) || 0;
                    setComisionTarjeta(comision);
                    const costoConfig = empresaResult.data.valores.empresa_campo_costo || 'precio_compra';
                    setCampoCosto(costoConfig);
                }

                if (logoResult.success && logoResult.data?.valor) {
                    let logoValue = logoResult.data.valor;
                    if (logoValue && !logoValue.startsWith('data:')) {
                        logoValue = `data:image/png;base64,${logoValue}`;
                    }
                    setEmpresaLogo(logoValue);
                }
            } catch (error) {
                console.error('Error cargando configuraciones:', error);
            }
        };
        cargarConfiguraciones();
    }, []);

    // Obtener impresora: usa la configurada o fallback a la del sistema
    const obtenerImpresora = async () => {
        const configurada = configImpresion.impresion_impresora_predeterminada;
        if (configurada) return configurada;

        if (window.electron?.ipcRenderer) {
            try {
                const result = await window.electron.ipcRenderer.invoke('get-default-printer');
                if (result.success && result.printer) return result.printer.name;
            } catch (e) { }
        }
        return 'Microsoft Print to PDF';
    };

    // Persistir ventas pausadas
    useEffect(() => {
        localStorage.setItem('pos_ventas_pausadas', JSON.stringify(ventasPausadas));
    }, [ventasPausadas]);

    // Auto-focus en búsqueda
    useEffect(() => {
        setTimeout(() => searchInputRef.current?.focus(), 100);
    }, []);

    // Auto-focus después de cerrar modales
    useEffect(() => {
        if (!showProductsModal && !showPriceSelector && !showQuantityModal && searchInputRef.current) {
            setTimeout(() => searchInputRef.current?.focus(), 100);
        }
    }, [showProductsModal, showPriceSelector, showQuantityModal]);

    // Cerrar selector de precios al hacer click fuera
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (showPriceSelector && !e.target.closest('.price-selector-container')) {
                setShowPriceSelector(null);
            }
        };
        if (showPriceSelector) {
            const timeoutId = setTimeout(() => {
                document.addEventListener('click', handleClickOutside);
            }, 0);
            return () => {
                clearTimeout(timeoutId);
                document.removeEventListener('click', handleClickOutside);
            };
        }
    }, [showPriceSelector]);

    // Búsqueda de productos
    useEffect(() => {
        const searchProducts = async () => {
            if (searchQuery.length >= 2) {
                try {
                    const esCodigoBarras = /^\d{6,}$/.test(searchQuery.trim());
                    const result = await apiService.buscarProductos(searchQuery);

                    if (result.success) {
                        const productos = result.data || [];

                        // Si es código de barras y hay exactamente 1 resultado con match exacto
                        if (esCodigoBarras && productos.length === 1) {
                            const producto = productos[0];
                            // Verificar que el código de barras coincida exactamente
                            if (producto.codigo_barras === searchQuery.trim()) {
                                // Agregar directo al carrito sin mostrar resultados
                                handleAddToCartDirectly(producto);
                                return;
                            }
                        }

                        // Ordenar resultados por relevancia
                        const queryNorm = searchQuery.trim().toLowerCase();
                        const queryWords = queryNorm.split(/\s+/);
                        // Normalizar: quitar # y ceros a la izquierda de números
                        const normalizarTexto = (txt) => txt.toLowerCase().replace(/#\s*/g, '').replace(/\b0+(\d)/g, '$1');
                        const queryNormNum = normalizarTexto(queryNorm);

                        const scored = productos.map(p => {
                            const nombre = (p.nombre || '').toLowerCase();
                            const nombreNorm = normalizarTexto(nombre);
                            let score = 0;

                            // Coincidencia exacta del query normalizado en nombre normalizado
                            if (nombreNorm.includes(queryNormNum)) score += 100;

                            // Nombre empieza con el query
                            if (nombreNorm.startsWith(queryNormNum)) score += 50;

                            // Todas las palabras del query aparecen en el nombre
                            const todasPresentes = queryWords.every(w => {
                                const wNorm = normalizarTexto(w);
                                return nombreNorm.includes(wNorm);
                            });
                            if (todasPresentes) score += 30;

                            // Nombre más corto = más específico = mejor match
                            score -= nombre.length * 0.1;

                            return { ...p, _score: score };
                        });

                        scored.sort((a, b) => b._score - a._score);

                        // Comportamiento normal: mostrar resultados
                        setSearchResults(scored);
                        setShowResults(true);
                    }
                } catch (error) {
                    console.error('Error buscando productos:', error);
                }
            } else {
                setSearchResults([]);
                setShowResults(false);
            }
            setSelectedSearchResultIndex(0);
        };

        // Delay más corto para códigos de barras (lectores son rápidos)
        const esCodigoBarras = /^\d{6,}$/.test(searchQuery.trim());
        const delay = esCodigoBarras ? 100 : 300;

        const timer = setTimeout(searchProducts, delay);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Scroll al item seleccionado
    useEffect(() => {
        if (showResults && searchResultsContainerRef.current && searchResults.length > 0) {
            const container = searchResultsContainerRef.current;
            const selectedElement = container.querySelector('.search-result-item.keyboard-focused');
            if (selectedElement) {
                selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
        }
    }, [selectedSearchResultIndex, showResults, searchResults.length]);

    // Navegación del selector de precios
    useEffect(() => {
        if (!showPriceSelector) return;

        const handlePriceSelectorKeyDown = (e) => {
            const precios = showPriceSelector.precios;
            const preciosDisponibles = [];
            if (precios.precio_lista > 0) preciosDisponibles.push({ tipo: 'precio_lista', valor: precios.precio_lista });
            if (precios.precio1 > 0) preciosDisponibles.push({ tipo: 'precio1', valor: precios.precio1 });
            if (precios.precio2 > 0) preciosDisponibles.push({ tipo: 'precio2', valor: precios.precio2 });
            if (precios.precio3 > 0) preciosDisponibles.push({ tipo: 'precio3', valor: precios.precio3 });
            if (precios.precio4 > 0) preciosDisponibles.push({ tipo: 'precio4', valor: precios.precio4 });
            if (precios.precio5 > 0) preciosDisponibles.push({ tipo: 'precio5', valor: precios.precio5 });
            if (precios.precio6 > 0) preciosDisponibles.push({ tipo: 'precio6', valor: precios.precio6 });

            // F4: Doble F4 para precio libre
            if (e.key === 'F4') {
                e.preventDefault();
                const now = Date.now();
                const timeSinceLastF4 = now - lastF4PressRef.current;

                if (timeSinceLastF4 < 500) {
                    // Doble F4 - Abrir precio libre
                    const item = cart.find(i => i.id === showPriceSelector.productId);
                    if (item) {
                        setShowPriceSelector(null);
                        lastF4PressRef.current = 0;
                        const precioCosto = item.precios?.[campoCosto] || item.precios?.precio_compra || 0;
                        setPrecioLibreData({
                            precio: '',
                            itemId: item.id,
                            precioCosto: precioCosto,
                            productoNombre: item.producto_nombre || item.nombre
                        });
                        if (hasPermission('ventas_precio_libre')) {
                            setShowPrecioLibreModal(true);
                            setTimeout(() => {
                                precioLibreInputRef.current?.focus();
                                precioLibreInputRef.current?.select();
                            }, 150);
                        } else {
                            setShowPasswordPrecioLibreModal(true);
                            setTimeout(() => {
                                passwordPrecioLibreInputRef.current?.focus();
                                passwordPrecioLibreInputRef.current?.select();
                            }, 150);
                        }
                    }
                } else {
                    lastF4PressRef.current = now;
                }
                return;
            }

            if (preciosDisponibles.length === 0) return;

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedPriceIndex(prev => Math.min(prev + 1, preciosDisponibles.length - 1));
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedPriceIndex(prev => Math.max(prev - 1, 0));
            }
            if (e.key === 'Enter') {
                e.preventDefault();
                const precioSeleccionado = preciosDisponibles[selectedPriceIndex];
                if (precioSeleccionado) {
                    changePriceLevel(showPriceSelector.productId, precioSeleccionado.valor, precioSeleccionado.tipo);
                    setShowPriceSelector(null);
                }
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                setShowPriceSelector(null);
            }
        };

        window.addEventListener('keydown', handlePriceSelectorKeyDown);
        return () => window.removeEventListener('keydown', handlePriceSelectorKeyDown);
    }, [showPriceSelector, selectedPriceIndex, changePriceLevel, cart, hasPermission]);

    // Buscar clientes cuando cambia clientSearch
    useEffect(() => {
        if (clientSearch.length >= 2) {
            searchClients(clientSearch);
        } else {
            setClientResults([]);
            setShowClientResults(false);
        }
    }, [clientSearch]);

    // Navegación de teclado para modal de cliente
    useEffect(() => {
        if (!showClientModal) return;

        const handleClientModalKeyDown = (e) => {
            if (showClientResults && clientResults.length > 0) {
                if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setSelectedClientIndex(prev => Math.min(prev + 1, clientResults.length - 1));
                } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setSelectedClientIndex(prev => Math.max(prev - 1, 0));
                }
            }
        };

        window.addEventListener('keydown', handleClientModalKeyDown);
        return () => window.removeEventListener('keydown', handleClientModalKeyDown);
    }, [showClientModal, showClientResults, clientResults]);

    // Atajos de teclado para modal de pago
    useEffect(() => {
        if (!showPaymentConfirmModal) return;

        const handlePaymentModalKeyDown = (e) => {
            if (e.key === 'e' || e.key === 'E') {
                e.preventDefault();
                agregarMetodoPago('efectivo');
            }
            if (e.key === 'c' || e.key === 'C') {
                e.preventDefault();
                agregarMetodoPago('tarjeta_credito');
            }
            if (e.key === 'd' || e.key === 'D') {
                e.preventDefault();
                agregarMetodoPago('tarjeta_debito');
            }
            if (e.key === 't' || e.key === 'T') {
                e.preventDefault();
                agregarMetodoPago('transferencia');
            }
            if (e.key === 'Enter') {
                e.preventDefault();
                const metodosConComision = ['tarjeta_credito', 'transferencia'];
                const tieneTarjetaOTransferencia = pagosDetalle.some(p => metodosConComision.includes(p.metodo));
                const aplicaComision = totals.tienePrecioMayoreo && comisionTarjeta > 0 && tieneTarjetaOTransferencia;
                const totalVenta = aplicaComision ? totals.totalConComision : totals.total;
                const totalPagado = pagosDetalle.reduce((sum, p) => sum + (parseFloat(p.monto) || 0), 0);
                const restante = Math.round((totalVenta - totalPagado) * 100) / 100;
                if (restante === 0 && !loading) {
                    confirmarPago();
                }
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                setShowPaymentConfirmModal(false);
                setPagosDetalle([]);
            }
        };

        window.addEventListener('keydown', handlePaymentModalKeyDown);
        return () => window.removeEventListener('keydown', handlePaymentModalKeyDown);
    }, [showPaymentConfirmModal, pagosDetalle, totals, loading, comisionTarjeta]);

    // Alt key para menú rápido
    useEffect(() => {
        const handleAltKey = (e) => {
            if (e.key === 'Alt') {
                e.preventDefault();
                setShowQuickMenu(prev => !prev);
            }
        };
        window.addEventListener('keydown', handleAltKey);
        return () => window.removeEventListener('keydown', handleAltKey);
    }, []);

    // Cerrar quick menu con click afuera
    useEffect(() => {
        if (showQuickMenu) {
            const handleClickOutside = (e) => {
                if (!e.target.closest('.quick-menu-panel')) {
                    setShowQuickMenu(false);
                }
            };
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [showQuickMenu]);

    // Escape cierra quick menu
    useEffect(() => {
        if (!showQuickMenu) return;
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                setShowQuickMenu(false);
            }
        };
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [showQuickMenu]);

    // Cargar totales de caja cuando se abre el menú
    useEffect(() => {
        if (showQuickMenu) {
            cargarTotalesCaja();
        }
    }, [showQuickMenu]);

    // Navegación con teclado en modal de ventas pausadas
    useEffect(() => {
        if (!showVentasPausadasModal) return;

        // Resetear índice cuando se abre el modal
        setSelectedVentaPausadaIndex(0);

        const handleVentasPausadasKeyDown = (e) => {
            if (ventasPausadas.length === 0) return;

            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    setSelectedVentaPausadaIndex(prev =>
                        prev < ventasPausadas.length - 1 ? prev + 1 : 0
                    );
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    setSelectedVentaPausadaIndex(prev =>
                        prev > 0 ? prev - 1 : ventasPausadas.length - 1
                    );
                    break;
                case 'Enter':
                    e.preventDefault();
                    if (ventasPausadas[selectedVentaPausadaIndex]) {
                        retomarVentaPausada(ventasPausadas[selectedVentaPausadaIndex]);
                    }
                    break;
                case 'Delete':
                    e.preventDefault();
                    if (ventasPausadas[selectedVentaPausadaIndex]) {
                        eliminarVentaPausada(ventasPausadas[selectedVentaPausadaIndex].id);
                    }
                    break;
                case 'Escape':
                    e.preventDefault();
                    setShowVentasPausadasModal(false);
                    break;
            }
        };

        window.addEventListener('keydown', handleVentasPausadasKeyDown);
        return () => window.removeEventListener('keydown', handleVentasPausadasKeyDown);
    }, [showVentasPausadasModal, ventasPausadas, selectedVentaPausadaIndex]);

    // ATAJOS DE TECLADO PRINCIPALES
    useEffect(() => {
        const handleKeyDown = (e) => {
            // No procesar si hay modales abiertos
            if (showProductsModal || showQuantityModal || showPaymentConfirmModal || showClientModal || showVentasPausadasModal || showPasswordModal || showRetiroModal || showReimprimirModal || showPasswordIngresoModal || showIngresoModal || showComandaModal || showPrecioLibreModal || showPasswordPrecioLibreModal || showHistorialModal) {
                return;
            }

            // Ctrl+H: Historial de producto-cliente
            if (e.ctrlKey && (e.key === 'h' || e.key === 'H')) {
                e.preventDefault();
                if (cliente && selectedCartItemId) {
                    const item = cart.find(i => i.id === selectedCartItemId);
                    if (item) {
                        handleAbrirHistorial(item);
                    }
                } else if (!cliente) {
                    showToast('Selecciona un cliente primero (F2)', 'warning');
                } else {
                    showToast('Selecciona un producto del carrito', 'warning');
                }
            }

            // F1: Focus en búsqueda
            if (e.key === 'F1') {
                e.preventDefault();
                searchInputRef.current?.focus();
            }

            // F2: Modal de cliente
            if (e.key === 'F2') {
                e.preventDefault();
                setShowClientModal(true);
            }

            // F3: Ventas pausadas
            if (e.key === 'F3') {
                e.preventDefault();
                setShowVentasPausadasModal(true);
            }

            // F4: Selector de precios / Doble F4: Precio libre
            if (e.key === 'F4' && selectedCartItemId) {
                const item = cart.find(i => i.id === selectedCartItemId);
                if (item && item.precios) {
                    e.preventDefault();
                    const now = Date.now();
                    const timeSinceLastF4 = now - lastF4PressRef.current;

                    // Doble F4 (dentro de 500ms) = Precio libre
                    if (timeSinceLastF4 < 500 && showPriceSelector) {
                        setShowPriceSelector(null);
                        lastF4PressRef.current = 0;
                        // Abrir precio libre
                        const precioCosto = item.precios?.[campoCosto] || item.precios?.precio_compra || 0;
                        setPrecioLibreData({
                            precio: '',
                            itemId: item.id,
                            precioCosto: precioCosto,
                            productoNombre: item.producto_nombre || item.nombre
                        });
                        // Verificar si tiene permiso
                        if (hasPermission('ventas_precio_libre')) {
                            setShowPrecioLibreModal(true);
                            setTimeout(() => {
                                precioLibreInputRef.current?.focus();
                                precioLibreInputRef.current?.select();
                            }, 100);
                        } else {
                            setShowPasswordPrecioLibreModal(true);
                            setTimeout(() => passwordPrecioLibreInputRef.current?.focus(), 100);
                        }
                    } else if (!showPriceSelector) {
                        // Primer F4 = Selector de precios normal
                        lastF4PressRef.current = now;
                        setSelectedPriceIndex(0);
                        setShowPriceSelector({
                            productId: item.id,
                            currentPrice: item.precio_unitario,
                            precios: item.precios,
                            position: null
                        });
                    } else {
                        // F4 con selector abierto pero fuera de tiempo = actualizar tiempo
                        lastF4PressRef.current = now;
                    }
                }
            }

            // F5: Pausar venta actual
            if (e.key === 'F5') {
                e.preventDefault();
                pausarVentaActual();
            }

            // F7: Abrir modal de retiro de efectivo (pide contraseña)
            if (e.key === 'F7') {
                e.preventDefault();
                setShowPasswordModal(true);
                setTimeout(() => passwordInputRef.current?.focus(), 100);
            }

            // F8: Abrir modal de reimprimir ticket
            if (e.key === 'F8') {
                e.preventDefault();
                handleAbrirReimprimirModal();
            }

            // F12: Abrir modal de ingreso de efectivo (pide contraseña)
            if (e.key === 'F12') {
                e.preventDefault();
                setShowPasswordIngresoModal(true);
                setTimeout(() => passwordIngresoInputRef.current?.focus(), 100);
            }

            // F9: Limpiar carrito
            if (e.key === 'F9') {
                e.preventDefault();
                clearCart();
                setCliente(null);
                setObservaciones('');
                setSearchQuery('');
                setSearchResults([]);
                searchInputRef.current?.focus();
            }

            // F10 o End: Procesar venta
            if ((e.key === 'End' || e.key === 'F10') && cart.length > 0 && !loading) {
                e.preventDefault();
                handleProcessSale();
            }

            // F11: Cambiar presentación
            if (e.key === 'F11' && selectedCartItemId) {
                const item = cart.find(i => i.id === selectedCartItemId);
                if (item && item.equivalencias_disponibles && item.equivalencias_disponibles.length > 1) {
                    e.preventDefault();
                    const currentEqIndex = item.equivalencias_disponibles.findIndex(eq => eq.producto_id === item.producto_id);
                    const nextIndex = (currentEqIndex + 1) % item.equivalencias_disponibles.length;
                    const nuevaPresentacion = item.equivalencias_disponibles[nextIndex];
                    cambiarPresentacion(item.id, nuevaPresentacion);
                    setSelectedCartItemId(nuevaPresentacion.producto_id);
                }
            }

            // Navegación del carrito
            if (!showResults && !showPriceSelector && cart.length > 0) {
                if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    const currentIndex = cart.findIndex(item => item.id === selectedCartItemId);
                    if (currentIndex === -1) {
                        setSelectedCartItemId(cart[0].id);
                    } else if (currentIndex < cart.length - 1) {
                        setSelectedCartItemId(cart[currentIndex + 1].id);
                    }
                }
                if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    const currentIndex = cart.findIndex(item => item.id === selectedCartItemId);
                    if (currentIndex === -1) {
                        setSelectedCartItemId(cart[cart.length - 1].id);
                    } else if (currentIndex > 0) {
                        setSelectedCartItemId(cart[currentIndex - 1].id);
                    }
                }
                if ((e.key === 'Delete' || e.key === 'Supr') && selectedCartItemId) {
                    e.preventDefault();
                    const currentIndex = cart.findIndex(item => item.id === selectedCartItemId);
                    deleteFromCart(selectedCartItemId);
                    if (cart.length > 1) {
                        if (currentIndex < cart.length - 1) {
                            setSelectedCartItemId(cart[currentIndex + 1]?.id || cart[currentIndex - 1]?.id);
                        } else {
                            setSelectedCartItemId(cart[currentIndex - 1]?.id || null);
                        }
                    } else {
                        setSelectedCartItemId(null);
                    }
                }
                if ((e.key === '+' || e.key === 'Add') && selectedCartItemId) {
                    e.preventDefault();
                    const item = cart.find(i => i.id === selectedCartItemId);
                    if (item) {
                        updateQuantity(selectedCartItemId, parseFloat(item.cantidad) + 1);
                    }
                }
                if ((e.key === '-' || e.key === 'Subtract') && selectedCartItemId) {
                    e.preventDefault();
                    const item = cart.find(i => i.id === selectedCartItemId);
                    if (item) {
                        const newQty = parseFloat(item.cantidad) - 1;
                        if (newQty > 0) {
                            updateQuantity(selectedCartItemId, newQty);
                        } else {
                            deleteFromCart(selectedCartItemId);
                            setSelectedCartItemId(null);
                        }
                    }
                }
            }

            // Navegación de resultados de búsqueda
            if (showResults && searchResults.length > 0) {
                if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setSelectedSearchResultIndex(prev => Math.min(prev + 1, searchResults.length - 1));
                }
                if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setSelectedSearchResultIndex(prev => Math.max(prev - 1, 0));
                }
                if (e.key === 'Enter' && searchResults[selectedSearchResultIndex]) {
                    e.preventDefault();
                    const producto = searchResults[selectedSearchResultIndex];
                    const esCodigoBarras = /^\d{6,}$/.test(searchQuery.trim());
                    if (esCodigoBarras) {
                        handleAddToCartDirectly(producto);
                    } else {
                        handleAddToCart(producto);
                    }
                    setSelectedSearchResultIndex(0);
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [showResults, searchResults, selectedSearchResultIndex, searchQuery, cart, selectedCartItemId, showProductsModal, showQuantityModal, showPaymentConfirmModal, showClientModal, showVentasPausadasModal, showPriceSelector, showComandaModal, showPrecioLibreModal, showPasswordPrecioLibreModal, loading, clearCart, cambiarPresentacion, deleteFromCart, updateQuantity, changePriceLevel, hasPermission]);

    // Funciones de cliente
    const removeClient = () => {
        setCliente(null);
        // Volver los productos a precio lista
        if (cart.length > 0) {
            setCart(prevCart => prevCart.map(item => {
                if (item.precios && item.precios.precio_lista > 0) {
                    return {
                        ...item,
                        precio_unitario: item.precios.precio_lista,
                        precio_tipo: 'precio_lista'
                    };
                }
                return item;
            }));
        }
        showToast('Cliente removido. Se usarán precios generales', 'info');
    };

    const searchClients = async (query) => {
        if (!query || query.trim().length < 2) {
            setClientResults([]);
            setShowClientResults(false);
            return;
        }
        try {
            const isOnlyNumbers = /^\d+$/.test(query);

            if (isOnlyNumbers && query.length === 10) {
                // Búsqueda por teléfono completo (10 dígitos)
                const result = await apiService.buscarClientePorTelefono(query.trim());
                if (result.success) {
                    setClientResults([result.data]);
                    setShowClientResults(true);
                    setSelectedClientIndex(0);
                } else {
                    setClientResults([]);
                    setShowClientResults(false);
                }
            } else {
                // Búsqueda por nombre
                const result = await apiService.buscarClientes(query.trim());
                if (result.success && result.data && result.data.length > 0) {
                    setClientResults(result.data);
                    setShowClientResults(true);
                    setSelectedClientIndex(0);
                } else {
                    setClientResults([]);
                    setShowClientResults(false);
                }
            }
        } catch (error) {
            console.error('Error buscando clientes:', error);
            setClientResults([]);
            setShowClientResults(false);
        }
    };

    const selectClient = (selectedCliente) => {
        setCliente(selectedCliente);
        setShowClientModal(false);
        setClientSearch('');
        setClientResults([]);
        setShowClientResults(false);

        // Actualizar precios del carrito según el precio del cliente
        if (cart.length > 0 && selectedCliente?.precio_venta) {
            const precioTipo = selectedCliente.precio_venta;
            setCart(prevCart => prevCart.map(item => {
                if (item.precios) {
                    // Obtener el precio correspondiente al tipo del cliente
                    let nuevoPrecio = item.precios[precioTipo] || 0;

                    // Si el precio del tipo del cliente es 0, buscar un precio válido
                    if (nuevoPrecio === 0) {
                        const preciosFallback = ['precio_lista', 'precio1', 'precio2', 'precio3', 'precio4', 'precio5', 'precio6'];
                        for (const tipo of preciosFallback) {
                            if (item.precios[tipo] > 0) {
                                nuevoPrecio = item.precios[tipo];
                                break;
                            }
                        }
                    }

                    return {
                        ...item,
                        precio_unitario: nuevoPrecio,
                        precio_tipo: precioTipo
                    };
                }
                return item;
            }));
        }

        setTimeout(() => {
            searchInputRef.current?.focus();
        }, 100);
    };

    // Funciones del carrito
    const handleAddToCart = async (producto) => {
        setSearchQuery('');
        setSearchResults([]);
        setShowResults(false);
        setEquivalenciasDisponibles([]);

        // Verificar si la unidad del producto está en la lista configurada
        const unidadProducto = (producto.unidad_medida || '').toLowerCase().trim();
        const requiereModal = unidadesModalCantidad.length > 0 && unidadesModalCantidad.includes(unidadProducto);

        if (!requiereModal) {
            handleAddToCartDirectly(producto);
            return;
        }

        setLoadingEquivalencias(true);
        const equivalencias = await loadEquivalencias(producto);
        setEquivalenciasDisponibles(equivalencias);
        setLoadingEquivalencias(false);

        setShowQuantityModal({
            producto: producto
        });
    };

    const handleAddToCartDirectly = async (producto) => {
        await addToCartDirectly(producto);
        setSearchQuery('');
        setSearchResults([]);
        setShowResults(false);
        setTimeout(() => searchInputRef.current?.focus(), 100);
    };

    const handlePriceClick = (item, event) => {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }
        if (item.precios) {
            const rect = event.currentTarget.getBoundingClientRect();
            setShowPriceSelector({
                productId: item.id,
                currentPrice: item.precio_unitario,
                precios: item.precios,
                position: { top: rect.bottom + 4, left: rect.left }
            });
        }
    };

    // Ventas pausadas
    const pausarVentaActual = () => {
        if (cart.length === 0) {
            showToast('No hay productos para pausar', 'warning');
            return;
        }
        const nuevaVentaPausada = {
            id: Date.now(),
            cart: [...cart],
            cliente,
            observaciones,
            fecha: new Date().toISOString()
        };
        setVentasPausadas(prev => [...prev, nuevaVentaPausada]);
        clearCart();
        setCliente(null);
        setObservaciones('');
        // showToast('Venta pausada', 'success');
        searchInputRef.current?.focus();
    };

    const retomarVentaPausada = (venta, eliminar = true) => {
        if (eliminar) {
            setVentasPausadas(prev => prev.filter(v => v.id !== venta.id));
        }
        setCart(venta.cart);
        setCliente(venta.cliente);
        setObservaciones(venta.observaciones || '');
        setShowVentasPausadasModal(false);
        // showToast('Venta retomada', 'success');
        setTimeout(() => searchInputRef.current?.focus(), 100);
    };

    const eliminarVentaPausada = (ventaId) => {
        setVentasPausadas(prev => prev.filter(v => v.id !== ventaId));
        showToast('Venta pausada eliminada', 'info');
    };

    // Procesar venta
    const handleProcessSale = () => {
        if (cart.length === 0) {
            showToast('Debe agregar al menos un producto', 'warning');
            return;
        }
        setPagosDetalle([]);
        setShowPaymentConfirmModal(true);
    };

    // Funciones de pago
    const agregarMetodoPago = (metodo) => {
        if (pagosDetalle.some(p => p.metodo === metodo)) {
            showToast('Método de pago ya agregado', 'warning');
            return;
        }

        // Calcular si aplica comisión
        const metodosConComision = ['tarjeta_credito', 'transferencia'];
        const tendraComision = totals.tienePrecioMayoreo && comisionTarjeta > 0 &&
            (metodosConComision.includes(metodo) || pagosDetalle.some(p => metodosConComision.includes(p.metodo)));
        const totalAUsar = tendraComision ? totals.totalConComision : totals.total;

        const totalPagado = pagosDetalle.reduce((sum, p) => sum + (parseFloat(p.monto) || 0), 0);
        const montoRestante = Math.round((totalAUsar - totalPagado) * 100) / 100;

        if (montoRestante <= 0) {
            showToast('El total ya está cubierto', 'warning');
            return;
        }

        setPagosDetalle([...pagosDetalle, {
            metodo: metodo,
            monto: montoRestante
        }]);
    };

    const eliminarMetodoPago = (index) => {
        setPagosDetalle(pagosDetalle.filter((_, i) => i !== index));
    };

    const actualizarMonto = (index, nuevoMonto) => {
        const metodosConComision = ['tarjeta_credito', 'transferencia'];
        const tieneComision = totals.tienePrecioMayoreo && comisionTarjeta > 0 &&
            pagosDetalle.some(p => metodosConComision.includes(p.metodo));
        const totalVenta = tieneComision ? totals.totalConComision : totals.total;

        const totalOtrosMetodos = pagosDetalle.reduce((sum, pago, i) => {
            if (i !== index) return sum + (parseFloat(pago.monto) || 0);
            return sum;
        }, 0);

        const disponible = totalVenta - totalOtrosMetodos;
        const montoLimitado = Math.round(Math.min(Math.max(nuevoMonto, 0), disponible) * 100) / 100;

        const nuevosPagos = [...pagosDetalle];
        nuevosPagos[index] = { ...nuevosPagos[index], monto: montoLimitado };
        setPagosDetalle(nuevosPagos);
    };

    const resetForm = () => {
        setCart([]);
        setCliente(null);
        setObservaciones('');
        setPagosDetalle([]);
        setSearchQuery('');
        setSearchResults([]);
        setShowResults(false);
        setSelectedCartItemId(null);
        setTimeout(() => {
            searchInputRef.current?.focus();
        }, 100);
    };

    const confirmarPago = async () => {
        if (cart.length === 0) {
            showToast('Debe agregar al menos un producto', 'warning');
            return;
        }

        if (!user) {
            showToast('No se encontró usuario activo', 'error');
            return;
        }

        if (pagosDetalle.length === 0) {
            showToast('Debe seleccionar al menos un método de pago', 'warning');
            return;
        }

        const metodosConComision = ['tarjeta_credito', 'transferencia'];
        const tieneComision = totals.tienePrecioMayoreo && comisionTarjeta > 0 &&
            pagosDetalle.some(p => metodosConComision.includes(p.metodo));
        const totalEsperado = tieneComision ? totals.totalConComision : totals.total;

        const totalPagado = pagosDetalle.reduce((sum, p) => sum + (parseFloat(p.monto) || 0), 0);

        if (Math.abs(totalPagado - totalEsperado) > 0.01) {
            showToast('El monto total de los pagos no coincide con el total de la venta', 'warning');
            return;
        }

        const userId = user.id || user.usuario_id || user.user_id;
        const userName = user.usuario || user.nombre || user.username || user.user;

        if (!userId) {
            showToast('No se pudo obtener el ID del usuario', 'error');
            return;
        }

        let metodoPagoFinal = null;
        let pagosDetalleFinal = null;

        if (pagosDetalle.length === 1) {
            metodoPagoFinal = pagosDetalle[0].metodo;
        } else if (pagosDetalle.length > 1) {
            metodoPagoFinal = 'mixto';
            pagosDetalleFinal = pagosDetalle;
        }

        const ventaData = {
            usuario_id: userId,
            usuario_nombre: userName,
            nombre_pc: localStorage.getItem('pos_nombre_pc') || null,
            cliente_id: cliente?.id || null,
            productos: cart.map(item => ({
                producto_id: item.producto_id,
                producto_nombre: item.producto_nombre || item.nombre,
                cantidad: item.cantidad,
                precio_unitario: item.precio_unitario,
                descuento: item.descuento || 0,
                impuesto: item.impuesto || 0,
                precio_tipo: item.precio_tipo || 'precio_lista',
                impresora: item.impresora || null
            })),
            metodo_pago: metodoPagoFinal,
            pagos_detalle: pagosDetalleFinal,
            estatus_pago: 'pagado',
            observaciones: observaciones,
            aplica_comision: tieneComision ? 1 : 0,
            comision_porcentaje: tieneComision ? comisionTarjeta : 0,
            comision_monto: tieneComision ? totals.montoComision : 0
        };

        // Verificar si la comanda está desactivada
        if (!configImpresion.impresion_imprimir_comanda) {
            // Comanda desactivada: procesar venta sin comanda
            setShowPaymentConfirmModal(false);
            await procesarVentaConComanda(ventaData, null);
            return;
        }

        // Verificar si debemos preguntar ubicación de comanda
        if (!configImpresion.impresion_preguntar_ubicacion) {
            // Cerrar modal de pago e imprimir directamente en ubicación predeterminada
            setShowPaymentConfirmModal(false);
            const ubicacionDefault = configImpresion.impresion_ubicacion_default === 'almacen' ? 'almacenes' : 'local';
            await procesarVentaConComanda(ventaData, ubicacionDefault);
        } else {
            // Obtener impresora predeterminada
            const printerName = await obtenerImpresora();
            setImpresoraPredeterminada(printerName);

            // Mostrar modal para elegir ubicación
            setVentaPendiente(ventaData);
            setShowPaymentConfirmModal(false);
            setShowComandaModal(true);
        }
    };

    // Procesar venta con comanda
    const procesarVentaConComanda = async (ventaData, ubicacionComanda) => {
        setShowComandaModal(false);

        try {
            setLoading(true);

            const result = await apiService.crearVentaMostrador(ventaData);

            if (result.success) {
                // Actualizar totales de caja en segundo plano
                cargarTotalesCaja();

                // Disparar evento para que el Header actualice la alerta de efectivo
                window.dispatchEvent(new CustomEvent('venta-realizada'));

                // Imprimir comanda según ubicación seleccionada
                if (ubicacionComanda === 'local') {
                    try {
                        await imprimirComandaLocal(result.data.venta_id, cart, result.data.folio);
                    } catch (comandaError) {
                        console.error('Error imprimiendo comanda local:', comandaError);
                        showToast('Venta creada pero error al imprimir comanda', 'warning');
                    }
                } else if (ubicacionComanda === 'almacenes') {
                    try {
                        await imprimirComandaAlmacenes(result.data.venta_id, cart, result.data.folio);
                    } catch (comandaError) {
                        console.error('Error imprimiendo comanda almacenes:', comandaError);
                        showToast('Venta creada pero error al enviar comanda a almacenes', 'warning');
                    }
                }

                // Generar e imprimir ticket de mostrador
                try {
                    const ventaId = result.data.id || result.data.venta_id;
                    const ticketResult = await apiService.imprimirTicketMostrador(ventaId);
                    if (ticketResult.success) {
                        const ticketContent = ticketResult.data?.ticket || ticketResult.ticket;
                        const codigoFacturacion = ticketResult.data?.codigo_facturacion || result.data?.codigo_facturacion || '';
                        if (ticketContent) {
                            await imprimirTicket(ticketContent, result.data.folio, codigoFacturacion);
                        }
                    }
                } catch (ticketError) {
                    showToast('Venta creada pero error al generar ticket', 'warning');
                }

                setVentaPendiente(null);
                resetForm();
            } else {
                showToast(result.message || 'Error al crear venta', 'error');
            }
        } catch (error) {
            console.error('Error creating sale:', error);
            showToast('Error al crear venta', 'error');
        } finally {
            setLoading(false);
        }
    };

    // === FUNCIONES DE IMPRESIÓN ===

    // Generar texto de comanda
    const generarTextoComanda = (productos, folio, titulo = 'COMANDA') => {
        const fecha = new Date().toLocaleString('es-MX');
        let texto = '';
        texto += `${titulo}\n`;
        texto += '====================================\n';
        texto += `Folio: ${folio}\n`;
        texto += `Fecha: ${fecha}\n`;
        if (cliente) {
            texto += `Cliente: ${cliente.nombre}\n`;
        }
        texto += '====================================\n';
        texto += 'PRODUCTOS:\n';
        texto += '------------------------------------\n';

        productos.forEach(item => {
            const cantidad = item.cantidad;
            const nombre = item.producto_nombre || item.nombre;
            texto += `${cantidad} x ${nombre}\n`;
        });

        texto += '====================================\n';
        if (observaciones) {
            texto += `OBSERVACIONES:\n${observaciones}\n`;
            texto += '====================================\n';
        }
        texto += '\n\n\n';

        return texto;
    };

    // Imprimir comanda en LOCAL
    const imprimirComandaLocal = async (ventaId, productos, folio) => {
        try {
            const comandaTexto = generarTextoComanda(productos, folio, 'COMANDA LOCAL');

            if (!window.electron?.ipcRenderer) {
                imprimirComandaNavegador(comandaTexto, folio);
                return;
            }

            const printerName = await obtenerImpresora();

            await enviarAImpresora(comandaTexto, printerName, folio);
        } catch (error) {
            console.error('Error en impresión de comanda local:', error);
        }
    };

    // Imprimir comanda a ALMACENES
    const imprimirComandaAlmacenes = async (ventaId, productos, folio) => {
        try {
            const productosPorImpresora = {};

            productos.forEach(item => {
                const impresora = item.impresora || '__default__';
                if (!productosPorImpresora[impresora]) {
                    productosPorImpresora[impresora] = [];
                }
                productosPorImpresora[impresora].push(item);
            });

            if (!window.electron?.ipcRenderer) {
                const comandaTexto = generarTextoComanda(productos, folio, 'COMANDA ALMACÉN');
                imprimirComandaNavegador(comandaTexto, folio);
                return;
            }

            for (const [impresora, productosImpresora] of Object.entries(productosPorImpresora)) {
                let printerName = impresora;

                if (impresora === '__default__' || impresora === '__local__') {
                    printerName = await obtenerImpresora();
                }

                const comandaTexto = generarTextoComanda(productosImpresora, folio, `COMANDA - ${printerName}`);
                await enviarAImpresora(comandaTexto, printerName, folio);
            }
        } catch (error) {
            console.error('Error en impresión de comanda almacenes:', error);
        }
    };

    // Enviar a impresora
    const enviarAImpresora = async (comandaTexto, printerName, folio) => {
        const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="utf-8">
                <title>Comanda - ${folio}</title>
                <style>
                    body {
                        font-family: 'Courier New', monospace;
                        font-size: 14px;
                        font-weight: bold;
                        color: #000;
                        margin: 0;
                        padding: 5px;
                        white-space: pre-wrap;
                        background: white;
                        line-height: 1.3;
                    }
                    @media print {
                        @page { margin: 0; size: 80mm auto; }
                        body { margin: 0; padding: 2px; }
                    }
                </style>
            </head>
            <body>${comandaTexto}</body>
            </html>
        `;

        const result = await window.electron.ipcRenderer.invoke('print-document', {
            printerName: printerName,
            htmlContent: htmlContent,
            silent: true,
            options: {
                margins: { marginType: 'none' },
                pageSize: 'A4'
            }
        });

        if (!result.success) {
            console.error('Error al imprimir comanda en', printerName, ':', result.message);
        }
    };

    // Respaldo para navegador
    const imprimirComandaNavegador = (comandaTexto, folio) => {
        const ventanaImpresion = window.open('', 'Comanda', 'width=400,height=600');
        if (ventanaImpresion) {
            ventanaImpresion.document.write(`
                <!DOCTYPE html>
                <html>
                <head>
                    <title>Comanda - ${folio}</title>
                    <style>
                        body {
                            font-family: 'Courier New', monospace;
                            font-size: 14px;
                            font-weight: bold;
                            margin: 10px;
                            white-space: pre-wrap;
                        }
                    </style>
                </head>
                <body>${comandaTexto}</body>
                </html>
            `);
            ventanaImpresion.document.close();
            ventanaImpresion.print();
        }
    };

    // Imprimir ticket de venta
    const imprimirTicket = async (ticketContent, folio, codigoFacturacion = '') => {
        if (!ticketContent) return;

        // Construir URL del QR con código y sucursal
        const portalUrl = configImpresion.impresion_portal_facturacion_url || '';
        let qrUrl = portalUrl;
        if (portalUrl && codigoFacturacion) {
            const sep = portalUrl.includes('?') ? '&' : '?';
            qrUrl = portalUrl + sep + 'codigo=' + encodeURIComponent(codigoFacturacion);
            if (configImpresion._sucursal) {
                qrUrl += '&sucursal=' + encodeURIComponent(configImpresion._sucursal);
            }
        }

        const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="utf-8">
                <title>Ticket - ${folio || ''}</title>
                <link href="https://fonts.googleapis.com/css2?family=Libre+Barcode+128+Text&display=swap" rel="stylesheet">
                <style>
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    @page { size: 80mm auto; margin: 0; }
                    body {
                        font-family: 'Courier New', Courier, monospace;
                        font-size: 12.5px;
                        font-weight: bold;
                        color: #000000;
                        margin: 0;
                        padding: 1mm 0mm 1mm ${configImpresion.impresion_margen_izquierdo ?? 4}mm;
                        white-space: pre-wrap;
                        background: white;
                        line-height: 1.4;
                        -webkit-font-smoothing: none;
                        -moz-osx-font-smoothing: unset;
                        text-rendering: geometricPrecision;
                        print-color-adjust: exact;
                        -webkit-print-color-adjust: exact;
                    }
                    .barcode-container { text-align: center; margin: 5mm 0; white-space: normal; max-width: 36ch; }
                    .barcode { font-family: 'Libre Barcode 128 Text', cursive; font-size: 48px; color: #000000; line-height: 1; }
                    .barcode-text { font-family: 'Courier New', monospace; font-size: 10px; margin-top: 2mm; }
                    .ticket-logo { text-align: center; margin-bottom: 2mm; white-space: normal; max-width: 36ch; }
                    .ticket-logo img { display: block; margin: 0 auto; max-height: 100px; max-width: 250px; image-rendering: -webkit-optimize-contrast; }
                </style>
            </head>
            <body>${empresaLogo ? `<div class="ticket-logo"><img src="${empresaLogo}" alt="Logo" /></div>` : ''}${configImpresion.impresion_encabezado_ticket ? `<div style="text-align:center;margin-bottom:3mm;white-space:pre-wrap;max-width:36ch;">${configImpresion.impresion_encabezado_ticket}</div>` : ''}${ticketContent}
${configImpresion.impresion_pie_ticket ? `<div style="text-align:center;margin-top:3mm;white-space:pre-wrap;max-width:36ch;">${configImpresion.impresion_pie_ticket}</div>` : ''}${configImpresion.impresion_codigo_barras !== false ? `<div class="barcode-container">
<div class="barcode">${folio || ''}</div>
<div class="barcode-text">${folio || ''}</div>
</div>` : ''}${configImpresion.impresion_leyenda_factura ? `<div style="text-align:center;margin-top:3mm;white-space:normal;max-width:36ch;font-size:11px;font-weight:bold;">${configImpresion.impresion_leyenda_factura}</div>` : ''}${(configImpresion.impresion_mostrar_qr_portal === true || configImpresion.impresion_mostrar_qr_portal === 'true') && portalUrl ? `<div style="text-align:center;margin-top:2mm;white-space:normal;max-width:36ch;"><img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(qrUrl)}" alt="QR" style="display:block;margin:0 auto;width:150px;height:150px;" /></div>` : ''}${codigoFacturacion ? `<div style="text-align:center;margin-top:2mm;white-space:normal;max-width:36ch;font-size:12px;font-weight:bold;font-family:'Courier New',monospace;">Código: ${codigoFacturacion}</div>` : ''}${configImpresion._sucursal ? `<div style="text-align:center;margin-top:1mm;white-space:normal;max-width:36ch;font-size:11px;font-family:'Courier New',monospace;">Sucursal: ${configImpresion._sucursal}</div>` : ''}
            </body>
            </html>
        `;

        if (!window.electron?.ipcRenderer) {
            const ventanaImpresion = window.open('', 'Ticket', 'width=400,height=600');
            if (ventanaImpresion) {
                ventanaImpresion.document.write(htmlContent);
                ventanaImpresion.document.close();
                setTimeout(() => ventanaImpresion.print(), 250);
            }
            return;
        }

        try {
            const printerName = await obtenerImpresora();

            await window.electron.ipcRenderer.invoke('print-document', {
                printerName: printerName,
                htmlContent: htmlContent,
                silent: true,
                options: { margins: { marginType: 'none' }, pageSize: 'A4', dpi: { horizontal: 203, vertical: 203 }, scaleFactor: 100 }
            });
        } catch (error) {
            const ventanaImpresion = window.open('', 'Ticket', 'width=400,height=600');
            if (ventanaImpresion) {
                ventanaImpresion.document.write(htmlContent);
                ventanaImpresion.document.close();
                setTimeout(() => ventanaImpresion.print(), 250);
            }
        }
    };

    // Imprimir ticket de retiro
    const imprimirTicketRetiro = async (monto, folio) => {
        const fecha = new Date();
        const fechaStr = fecha.toLocaleDateString('es-MX');
        const horaStr = fecha.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
        const nombrePc = localStorage.getItem('pos_nombre_pc') || 'N/A';
        const usuarioEnCaja = user?.usuario || user?.nombre || user?.username || 'N/A';

        const ticketContent = `
    RETIRO DE EFECTIVO
====================================
Folio: ${folio || 'N/A'}
Fecha: ${fechaStr} ${horaStr}
====================================
Caja: ${nombrePc}
Usuario en caja: ${usuarioEnCaja}
Autorizó: ${usuarioAutorizador || 'N/A'}
====================================

CANTIDAD:    ${formatMoney(monto)}

====================================
`;

        const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="utf-8">
                <title>Retiro - ${folio || ''}</title>
                <style>
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    @page { size: 80mm auto; margin: 0; }
                    body {
                        font-family: 'Courier New', monospace;
                        font-size: 12.5px;
                        font-weight: bold;
                        color: #000000;
                        margin: 0;
                        padding: 1mm 0mm 1mm ${configImpresion.impresion_margen_izquierdo ?? 4}mm;
                        white-space: pre-wrap;
                        background: white;
                        line-height: 1.4;
                        -webkit-font-smoothing: none;
                        text-rendering: geometricPrecision;
                        print-color-adjust: exact;
                        -webkit-print-color-adjust: exact;
                    }
                </style>
            </head>
            <body>${ticketContent}</body>
            </html>
        `;

        if (!window.electron?.ipcRenderer) {
            const ventanaImpresion = window.open('', 'Retiro', 'width=400,height=600');
            if (ventanaImpresion) {
                ventanaImpresion.document.write(htmlContent);
                ventanaImpresion.document.close();
                setTimeout(() => ventanaImpresion.print(), 250);
            }
            return;
        }

        try {
            const printerName = await obtenerImpresora();

            await window.electron.ipcRenderer.invoke('print-document', {
                printerName: printerName,
                htmlContent: htmlContent,
                silent: true,
                options: { margins: { marginType: 'none' }, pageSize: 'A4', dpi: { horizontal: 203, vertical: 203 }, scaleFactor: 100 }
            });
        } catch (error) {
            console.error('Error imprimiendo ticket de retiro:', error);
        }
    };

    // Imprimir ticket de ingreso
    const imprimirTicketIngreso = async (monto, concepto, folio) => {
        const fecha = new Date();
        const fechaStr = fecha.toLocaleDateString('es-MX');
        const horaStr = fecha.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
        const nombrePc = localStorage.getItem('pos_nombre_pc') || 'N/A';
        const usuarioEnCaja = user?.usuario || user?.nombre || user?.username || 'N/A';

        const conceptoTexto = {
            'fondo_caja': 'Fondo de Caja',
            'cambio': 'Cambio',
            'otro': 'Otro'
        }[concepto] || concepto;

        const ticketContent = `
    INGRESO DE EFECTIVO
====================================
Folio: ${folio || 'N/A'}
Fecha: ${fechaStr} ${horaStr}
====================================
Caja: ${nombrePc}
Usuario en caja: ${usuarioEnCaja}
Autorizó: ${usuarioAutorizadorIngreso || 'N/A'}
====================================
Concepto: ${conceptoTexto}

CANTIDAD:    ${formatMoney(monto)}

====================================
`;

        const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="utf-8">
                <title>Ingreso - ${folio || ''}</title>
                <style>
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    @page { size: 80mm auto; margin: 0; }
                    body {
                        font-family: 'Courier New', monospace;
                        font-size: 12.5px;
                        font-weight: bold;
                        color: #000000;
                        margin: 0;
                        padding: 1mm 0mm 1mm ${configImpresion.impresion_margen_izquierdo ?? 4}mm;
                        white-space: pre-wrap;
                        background: white;
                        line-height: 1.4;
                        -webkit-font-smoothing: none;
                        text-rendering: geometricPrecision;
                        print-color-adjust: exact;
                        -webkit-print-color-adjust: exact;
                    }
                </style>
            </head>
            <body>${ticketContent}</body>
            </html>
        `;

        if (!window.electron?.ipcRenderer) {
            const ventanaImpresion = window.open('', 'Ingreso', 'width=400,height=600');
            if (ventanaImpresion) {
                ventanaImpresion.document.write(htmlContent);
                ventanaImpresion.document.close();
                setTimeout(() => ventanaImpresion.print(), 250);
            }
            return;
        }

        try {
            const printerName = await obtenerImpresora();

            await window.electron.ipcRenderer.invoke('print-document', {
                printerName: printerName,
                htmlContent: htmlContent,
                silent: true,
                options: { margins: { marginType: 'none' }, pageSize: 'A4', dpi: { horizontal: 203, vertical: 203 }, scaleFactor: 100 }
            });
        } catch (error) {
            console.error('Error imprimiendo ticket de ingreso:', error);
        }
    };

    const handleOpenProductsModal = () => {
        setShowProductsModal(true);
    };

    const handleCloseProductsModal = () => {
        setShowProductsModal(false);
        searchInputRef.current?.focus();
    };

    // === FUNCIONES DE CAJA ===

    // Cargar totales de caja desde el último corte
    const cargarTotalesCaja = async () => {
        setTotalesCaja(prev => ({ ...prev, loading: true }));
        try {
            const nombrePc = localStorage.getItem('pos_nombre_pc') || null;
            const [ventasResult, ingresosResult] = await Promise.all([
                apiService.getVentasPendientesCorte(nombrePc),
                apiService.getIngresosPendientesCorte(nombrePc)
            ]);

            const totalesVentas = ventasResult.success && ventasResult.data?.totales ? ventasResult.data.totales : {};
            const totalesIngresos = ingresosResult.success ? ingresosResult.data : {};

            const efectivoBruto = totalesVentas.total_efectivo_bruto ?? totalesVentas.total_efectivo ?? 0;
            const ingresos = totalesIngresos.total_ingresos || 0;
            const retiros = totalesVentas.total_retiros || 0;
            const efectivoNeto = efectivoBruto + ingresos - retiros;

            setTotalesCaja({
                total_efectivo: efectivoNeto,
                total_efectivo_bruto: efectivoBruto,
                total_retiros: retiros,
                cantidad_retiros: totalesVentas.cantidad_retiros || 0,
                total_ingresos: ingresos,
                cantidad_ingresos: totalesIngresos.cantidad_ingresos || 0,
                total_tarjeta: totalesVentas.total_tarjeta || 0,
                total_transferencia: totalesVentas.total_transferencia || 0,
                total_ventas: totalesVentas.total_ventas || 0,
                cantidad_ventas: totalesVentas.cantidad_ventas || 0,
                loading: false
            });
        } catch (error) {
            console.error('Error cargando totales de caja:', error);
            setTotalesCaja(prev => ({ ...prev, loading: false }));
        }
    };

    // === FUNCIONES DE RETIRO DE EFECTIVO (F7) ===

    const handleVerificarPasswordRetiro = async () => {
        if (!passwordRetiro) {
            showToast('Ingresa tu contraseña', 'error');
            return;
        }

        setVerificandoPassword(true);
        try {
            const result = await apiService.verificarPermisoRetiro(passwordRetiro);

            if (result.success) {
                setUsuarioAutorizador(result.data?.usuario || result.data?.nombre || 'N/A');
                setShowPasswordModal(false);
                setPasswordRetiro('');
                setShowRetiroModal(true);
            } else {
                showToast(result.message || 'Acceso denegado', 'error');
            }
        } catch (error) {
            console.error('Error verificando permiso:', error);
            showToast('Error al verificar permiso', 'error');
        } finally {
            setVerificandoPassword(false);
        }
    };

    const handleRetiroEfectivo = async () => {
        const monto = parseFloat(retiroData.monto);

        if (!monto || monto <= 0) {
            showToast('Ingresa un monto válido', 'error');
            return;
        }

        if (monto > totalesCaja.total_efectivo) {
            showToast('El monto excede el efectivo disponible en caja', 'error');
            return;
        }

        setSavingRetiro(true);
        try {
            const nombrePc = localStorage.getItem('pos_nombre_pc');
            const result = await apiService.crearRetiroEfectivo({
                usuario_id: user?.user_id,
                monto: monto,
                motivo: retiroData.motivo || null,
                nombre_pc: nombrePc
            });

            if (result.success) {
                // Imprimir ticket de retiro
                const folioRetiro = result.data?.folio || result.data?.id || null;
                await imprimirTicketRetiro(monto, folioRetiro);

                setShowRetiroModal(false);
                setRetiroData({ monto: '', motivo: '' });
                setUsuarioAutorizador(null);
                cargarTotalesCaja();
                window.dispatchEvent(new CustomEvent('venta-realizada'));
            } else {
                showToast(result.message || 'Error al registrar retiro', 'error');
            }
        } catch (error) {
            console.error('Error en retiro:', error);
            showToast('Error al procesar el retiro', 'error');
        } finally {
            setSavingRetiro(false);
        }
    };

    // === FUNCIONES DE INGRESO DE EFECTIVO (F12) ===

    const handleVerificarPasswordIngreso = async () => {
        if (!passwordIngreso) {
            showToast('Ingresa tu contraseña', 'error');
            return;
        }

        setVerificandoPasswordIngreso(true);
        try {
            const result = await apiService.verificarPermisoIngreso(passwordIngreso);

            if (result.success) {
                setUsuarioAutorizadorIngreso(result.data?.usuario_nombre || result.data?.nombre || 'N/A');
                setShowPasswordIngresoModal(false);
                setPasswordIngreso('');
                setShowIngresoModal(true);
            } else {
                showToast(result.message || 'Acceso denegado', 'error');
            }
        } catch (error) {
            console.error('Error verificando permiso:', error);
            showToast('Error al verificar permiso', 'error');
        } finally {
            setVerificandoPasswordIngreso(false);
        }
    };

    const handleIngresoEfectivo = async () => {
        const monto = parseFloat(ingresoData.monto);

        if (!monto || monto <= 0) {
            showToast('Ingresa un monto válido', 'error');
            return;
        }

        if (!ingresoData.concepto) {
            showToast('Selecciona un concepto', 'error');
            return;
        }

        setSavingIngreso(true);
        try {
            const nombrePc = localStorage.getItem('pos_nombre_pc');
            const result = await apiService.crearIngresoEfectivo({
                usuario_id: user?.user_id,
                monto: monto,
                concepto: ingresoData.concepto,
                descripcion: ingresoData.descripcion || null,
                nombre_pc: nombrePc
            });

            if (result.success) {
                // Imprimir ticket de ingreso
                const folioIngreso = result.data?.folio || result.data?.id || null;
                await imprimirTicketIngreso(monto, ingresoData.concepto, folioIngreso);

                setShowIngresoModal(false);
                setIngresoData({ monto: '', concepto: 'fondo_caja', descripcion: '' });
                setUsuarioAutorizadorIngreso(null);
                cargarTotalesCaja();
                window.dispatchEvent(new CustomEvent('venta-realizada'));
            } else {
                showToast(result.message || 'Error al registrar ingreso', 'error');
            }
        } catch (error) {
            console.error('Error en ingreso:', error);
            showToast('Error al procesar el ingreso', 'error');
        } finally {
            setSavingIngreso(false);
        }
    };

    // === FUNCIONES DE REIMPRIMIR TICKET (F8) ===

    const handleAbrirReimprimirModal = async () => {
        setShowReimprimirModal(true);
        setShowQuickMenu(false);
        setLoadingVentas(true);
        try {
            const nombrePc = localStorage.getItem('pos_nombre_pc');
            const result = await apiService.getVentasPendientesCorte(nombrePc);
            if (result.success) {
                setVentasParaReimprimir(result.data.ventas || []);
            } else {
                setVentasParaReimprimir([]);
            }
        } catch (error) {
            console.error('Error cargando ventas:', error);
            setVentasParaReimprimir([]);
        } finally {
            setLoadingVentas(false);
        }
    };

    const handleReimprimirTicket = async (venta) => {
        setReimprimiendo(venta.id);
        try {
            const result = await apiService.imprimirTicketMostrador(venta.id);

            if (result.success) {
                const ticketContent = result.data?.ticket || result.ticket;
                if (ticketContent) {
                    const ticketConCopia = `********** COPIA **********\n\n${ticketContent}`;

                    if (window.electron?.ipcRenderer) {
                        const printerName = await obtenerImpresora();

                        const htmlContent = `
                            <!DOCTYPE html>
                            <html>
                            <head>
                                <meta charset="utf-8">
                                <style>
                                    * { margin: 0; padding: 0; box-sizing: border-box; }
                                    @page { size: 80mm auto; margin: 0; }
                                    body {
                                        font-family: 'Courier New', Courier, monospace;
                                        font-size: 12.5px;
                                        font-weight: bold;
                                        color: #000000;
                                        margin: 0;
                                        padding: 1mm 0mm 1mm ${configImpresion.impresion_margen_izquierdo ?? 4}mm;
                                        white-space: pre-wrap;
                                        background: white;
                                        line-height: 1.4;
                                    }
                                    .ticket-logo { text-align: center; margin-bottom: 2mm; white-space: normal; max-width: 36ch; }
                                    .ticket-logo img { display: block; margin: 0 auto; max-height: 100px; max-width: 250px; }
                                </style>
                            </head>
                            <body>${empresaLogo ? `<div class="ticket-logo"><img src="${empresaLogo}" alt="Logo" /></div>` : ''}${configImpresion.impresion_encabezado_ticket ? `<div style="text-align:center;margin-bottom:3mm;white-space:pre-wrap;max-width:36ch;">${configImpresion.impresion_encabezado_ticket}</div>` : ''}${ticketConCopia}
${configImpresion.impresion_pie_ticket ? `<div style="text-align:center;margin-top:3mm;white-space:pre-wrap;max-width:36ch;">${configImpresion.impresion_pie_ticket}</div>` : ''}${configImpresion.impresion_leyenda_factura ? `<div style="text-align:center;margin-top:3mm;white-space:normal;max-width:36ch;font-size:11px;font-weight:bold;">${configImpresion.impresion_leyenda_factura}</div>` : ''}${(configImpresion.impresion_mostrar_qr_portal === true || configImpresion.impresion_mostrar_qr_portal === 'true') && configImpresion.impresion_portal_facturacion_url ? `<div style="text-align:center;margin-top:2mm;white-space:normal;max-width:36ch;"><img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(configImpresion.impresion_portal_facturacion_url)}" alt="QR" style="display:block;margin:0 auto;width:150px;height:150px;" /></div>` : ''}</body>
                            </html>
                        `;

                        await window.electron.ipcRenderer.invoke('print-document', {
                            printerName: printerName,
                            htmlContent: htmlContent,
                            silent: true,
                            options: { margins: { marginType: 'none' }, pageSize: 'A4', dpi: { horizontal: 203, vertical: 203 }, scaleFactor: 100 }
                        });
                    }

                    // showToast(`Copia de ticket ${venta.folio} enviada a imprimir`, 'success');
                    setShowReimprimirModal(false);
                } else {
                    showToast('No se pudo obtener el contenido del ticket', 'error');
                }
            } else {
                showToast(result.message || 'Error al reimprimir ticket', 'error');
            }
        } catch (error) {
            showToast('Error al reimprimir ticket', 'error');
        } finally {
            setReimprimiendo(null);
        }
    };

    // === FUNCIONES DE PRECIO LIBRE (doble F4) ===

    const handleVerificarPasswordPrecioLibre = async () => {
        if (!passwordPrecioLibre) {
            showToast('Ingresa tu contraseña', 'error');
            return;
        }

        setVerificandoPasswordPrecioLibre(true);
        try {
            const result = await apiService.verificarPermisoPrecioLibre(passwordPrecioLibre);

            if (result.success) {
                setUsuarioAutorizadorPrecioLibre(result.data?.usuario_nombre || result.data?.nombre || 'N/A');
                setShowPasswordPrecioLibreModal(false);
                setPasswordPrecioLibre('');
                setShowPrecioLibreModal(true);
                setTimeout(() => {
                    precioLibreInputRef.current?.focus();
                    precioLibreInputRef.current?.select();
                }, 100);
            } else {
                showToast(result.message || 'Acceso denegado', 'error');
            }
        } catch (error) {
            console.error('Error verificando permiso:', error);
            showToast('Error al verificar permiso', 'error');
        } finally {
            setVerificandoPasswordPrecioLibre(false);
        }
    };

    const handleAplicarPrecioLibre = () => {
        const precioNuevo = parseFloat(precioLibreData.precio);

        if (!precioNuevo || precioNuevo <= 0) {
            showToast('Ingresa un precio válido', 'error');
            return;
        }

        if (precioNuevo < precioLibreData.precioCosto) {
            showToast(`El precio no puede ser menor al costo (${formatMoney(precioLibreData.precioCosto)})`, 'error');
            return;
        }

        // Aplicar el precio libre al item del carrito
        setCart(prevCart => prevCart.map(item => {
            if (item.id === precioLibreData.itemId) {
                return {
                    ...item,
                    precio_unitario: precioNuevo,
                    precio_tipo: 'precio_libre'
                };
            }
            return item;
        }));

        showToast(`Precio libre aplicado: ${formatMoney(precioNuevo)}`, 'success');
        cerrarPrecioLibre();
    };

    const cerrarPrecioLibre = () => {
        setShowPrecioLibreModal(false);
        setShowPasswordPrecioLibreModal(false);
        setPrecioLibreData({ precio: '', itemId: null, precioCosto: 0, productoNombre: '' });
        setPasswordPrecioLibre('');
        setUsuarioAutorizadorPrecioLibre(null);
        setTimeout(() => searchInputRef.current?.focus(), 100);
    };

    // === HISTORIAL PRODUCTO-CLIENTE (Ctrl+H) ===
    const handleAbrirHistorial = async (item) => {
        setHistorialProductoNombre(item.producto_nombre || item.nombre);
        setHistorialItemId(item.id);
        setHistorialData([]);
        setHistorialCostoActual(0);
        setHistorialCostoTipoActual('');
        setShowHistorialModal(true);
        setLoadingHistorial(true);
        try {
            const result = await apiService.getHistorialProductoCliente(item.producto_id, cliente.id);
            if (result.success) {
                setHistorialData(result.data?.historial || []);
                setHistorialCostoActual(result.data?.costo_actual || 0);
                setHistorialCostoTipoActual(result.data?.costo_tipo_actual || '');
            } else {
                showToast(result.message || 'Error al obtener historial', 'error');
            }
        } catch (error) {
            console.error('Error obteniendo historial:', error);
            showToast('Error al obtener historial', 'error');
        } finally {
            setLoadingHistorial(false);
        }
    };

    const aplicarPrecioHistorial = (precio) => {
        if (!historialItemId) return;
        setCart(prevCart => prevCart.map(item => {
            if (item.id === historialItemId) {
                return {
                    ...item,
                    precio_unitario: precio,
                    precio_tipo: 'precio_libre'
                };
            }
            return item;
        }));
        showToast(`Precio ${formatMoney(precio)} aplicado del historial`, 'success');
        setShowHistorialModal(false);
    };

    return (
        <div className="counter-sale-page" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div className="new-sale-form-table" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Header con info de cliente */}
                {cliente && (
                    <div style={{
                        padding: '12px 16px',
                        background: '#f0fdf4',
                        border: '1px solid #86efac',
                        borderRadius: '8px',
                        marginBottom: '16px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <User size={20} style={{ color: '#16a34a' }} />
                            <div>
                                <div style={{ fontWeight: 600, color: '#166534' }}>
                                    {cliente.nombre}
                                    {cliente.nombre_comercial && (
                                        <span style={{ fontWeight: 400, fontStyle: 'italic', marginLeft: '8px', color: '#15803d' }}>
                                            ({cliente.nombre_comercial})
                                        </span>
                                    )}
                                </div>
                                <div style={{ fontSize: '15px', color: '#15803d' }}>
                                    Precio: {cliente.precio_venta === 'precio1' ? 'P1' :
                                        cliente.precio_venta === 'precio2' ? 'P2' :
                                            cliente.precio_venta === 'precio3' ? 'P3' :
                                                cliente.precio_venta === 'precio4' ? 'P4' : 'P1'}
                                </div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <Button variant="ghost" size="small" onClick={() => setShowClientModal(true)}>
                                Cambiar Cliente
                            </Button>
                            <Button variant="ghost" size="small" onClick={removeClient}>
                                <X size={16} />
                            </Button>
                        </div>
                    </div>
                )}

                {/* Barra de búsqueda */}
                <div className="search-section-table">
                    <div className="search-box-large" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <div style={{ flex: 1, position: 'relative' }}>
                            <Input
                                ref={searchInputRef}
                                placeholder="Buscar producto por nombre o código de barras..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                icon={<Search size={24} />}
                                autoComplete="off"
                            />
                            {showResults && searchResults.length > 0 && (
                                <div className="search-results-dropdown" ref={searchResultsContainerRef}>
                                    <div className="search-hint">
                                        Use ↑↓ para navegar, <kbd>Enter</kbd> para agregar
                                    </div>
                                    {searchResults.map((producto, index) => (
                                        <div
                                            key={producto.id}
                                            className={`search-result-item compact ${selectedSearchResultIndex === index ? 'keyboard-focused' : ''}`}
                                            onClick={() => handleAddToCart(producto)}
                                        >
                                            {producto.codigo_corto && (
                                                <span className="result-code-short">{producto.codigo_corto}</span>
                                            )}
                                            <span className="result-name">{producto.nombre}</span>
                                            <span className="result-price">{formatMoney(getCustomerPrice(producto))}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {showResults && searchResults.length === 0 && searchQuery.length >= 2 && (
                                <div className="search-results-dropdown">
                                    <div className="no-results">No se encontraron productos</div>
                                </div>
                            )}
                        </div>
                        {!cliente && (
                            <Button onClick={() => setShowClientModal(true)} variant="outline" style={{ whiteSpace: 'nowrap', minWidth: '140px' }}>
                                <User size={18} style={{ marginRight: '6px' }} />
                                Seleccionar Cliente
                            </Button>
                        )}
                        <Button onClick={handleOpenProductsModal} variant="outline" style={{ whiteSpace: 'nowrap', minWidth: '120px' }}>
                            <Package size={18} style={{ marginRight: '6px' }} />
                            Catálogo de Productos
                        </Button>
                    </div>
                </div>

                {/* Tabla de productos */}
                <div className="products-table-container" style={{ flex: 1, overflow: 'auto' }}>
                    {cart.length === 0 ? (
                        <div className="empty-cart-table">
                            <Package size={64} />
                            <p>No hay productos en el pedido</p>
                            <span>Busca y agrega productos para continuar</span>
                        </div>
                    ) : (
                        <table className="products-table">
                            <thead>
                                <tr>
                                    <th>Cantidad</th>
                                    <th>Artículo</th>
                                    <th style={{ textAlign: 'left' }}>Descripción</th>
                                    <th>Unidad</th>
                                    <th style={{ textAlign: 'right' }}>Precio</th>
                                    <th>Total</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cart.map(item => (
                                    <tr
                                        key={item.id}
                                        onClick={() => setSelectedCartItemId(item.id)}
                                        style={{
                                            cursor: 'pointer',
                                            backgroundColor: selectedCartItemId === item.id ? '#eff6ff' : 'transparent',
                                            outline: selectedCartItemId === item.id ? '2px solid #3b82f6' : 'none',
                                            outlineOffset: '-2px',
                                            transition: 'background-color 0.15s, outline 0.15s'
                                        }}
                                    >
                                        <td>
                                            <div className="quantity-controls-table">
                                                <button className="qty-btn-table" onClick={() => removeFromCart(item.id)}>
                                                    <Minus size={14} />
                                                </button>
                                                <input
                                                    type="number"
                                                    className="qty-input-table"
                                                    value={item.cantidad}
                                                    onChange={(e) => updateQuantityDebounced(item.id, e.target.value)}
                                                    onFocus={(e) => e.target.select()}
                                                    min="0"
                                                    step="0.01"
                                                />
                                                <button className="qty-btn-table" onClick={() => updateQuantity(item.id, parseFloat(item.cantidad) + 1)}>
                                                    <Plus size={14} />
                                                </button>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="article-code">{item.codigo_corto || item.producto_id}</span>
                                        </td>
                                        <td style={{ textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            <span className="product-description">{item.producto_nombre || item.nombre}</span>
                                        </td>
                                        <td>
                                            {item.equivalencias_disponibles && item.equivalencias_disponibles.length > 1 ? (
                                                <select
                                                    value={item.producto_id}
                                                    onChange={(e) => {
                                                        const nuevaPresentacion = item.equivalencias_disponibles.find(
                                                            eq => eq.producto_id === parseInt(e.target.value)
                                                        );
                                                        if (nuevaPresentacion) {
                                                            cambiarPresentacion(item.id, nuevaPresentacion);
                                                        }
                                                    }}
                                                    style={{
                                                        fontSize: '15px',
                                                        padding: '6px 8px',
                                                        borderRadius: '4px',
                                                        border: '1px solid #3b82f6',
                                                        background: '#eff6ff',
                                                        color: '#1e40af',
                                                        fontWeight: 600,
                                                        cursor: 'pointer',
                                                        maxWidth: '100px'
                                                    }}
                                                    title="Cambiar presentación"
                                                >
                                                    {item.equivalencias_disponibles.map((eq) => (
                                                        <option key={eq.producto_id} value={eq.producto_id}>
                                                            {eq.unidad_medida || 'pz'}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <span className="unit-label" style={{ fontSize: '16px', color: '#6b7280', fontWeight: 500 }}>
                                                    {item.unidad_medida || 'pz'}
                                                </span>
                                            )}
                                        </td>
                                        <td className="text-right" style={{ position: 'relative' }}>
                                            <div className="price-selector-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px' }}>
                                                <button
                                                    onClick={(e) => handlePriceClick(item, e)}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        color: '#667eea',
                                                        fontWeight: 600,
                                                        padding: '6px 10px',
                                                        borderRadius: '4px',
                                                        transition: 'background 0.2s',
                                                        fontSize: '17px'
                                                    }}
                                                    title="Click para cambiar precio"
                                                >
                                                    {formatMoney(item.precio_unitario)}
                                                </button>
                                                {item.descuento_volumen > 0 && (
                                                    <span style={{
                                                        fontSize: '13px',
                                                        fontWeight: 600,
                                                        padding: '3px 8px',
                                                        borderRadius: '4px',
                                                        color: 'white',
                                                        backgroundColor: '#16a34a',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        -{item.descuento_volumen}%
                                                    </span>
                                                )}
                                                {item.precio_tipo && (
                                                    <span style={{
                                                        fontSize: '13px',
                                                        fontWeight: 600,
                                                        padding: '3px 8px',
                                                        borderRadius: '4px',
                                                        color: 'white',
                                                        backgroundColor:
                                                            item.precio_tipo === 'precio_libre' ? '#dc2626' :
                                                                item.precio_tipo === 'precio_lista' ? '#0284c7' :
                                                                    item.precio_tipo === 'precio1' ? '#1e40af' :
                                                                        item.precio_tipo === 'precio2' ? '#059669' :
                                                                            item.precio_tipo === 'precio3' ? '#b45309' :
                                                                                item.precio_tipo === 'precio4' ? '#be123c' :
                                                                                    item.precio_tipo === 'precio5' ? '#0891b2' :
                                                                                        item.precio_tipo === 'precio6' ? '#7e22ce' : '#0284c7',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {item.precio_tipo === 'precio_libre' ? 'LIBRE' :
                                                            item.precio_tipo === 'precio_lista' ? 'PL' :
                                                                item.precio_tipo === 'precio1' ? 'P1' :
                                                                    item.precio_tipo === 'precio2' ? 'P2' :
                                                                        item.precio_tipo === 'precio3' ? 'P3' :
                                                                            item.precio_tipo === 'precio4' ? 'P4' :
                                                                                item.precio_tipo === 'precio5' ? 'P5' :
                                                                                    item.precio_tipo === 'precio6' ? 'P6' : 'PL'}
                                                    </span>
                                                )}
                                                {/* Selector de precios inline */}
                                                {showPriceSelector && showPriceSelector.productId === item.id && (() => {
                                                    const precios = showPriceSelector.precios;
                                                    const preciosConfig = [
                                                        { tipo: 'precio_lista', label: 'Precio Lista', valor: precios.precio_lista, color: '#0284c7', bgSelected: '#e0f2fe' },
                                                        { tipo: 'precio1', label: 'Precio 1', valor: precios.precio1, color: '#1e40af', bgSelected: '#eff6ff' },
                                                        { tipo: 'precio2', label: 'Precio 2', valor: precios.precio2, color: '#065f46', bgSelected: '#d1fae5' },
                                                        { tipo: 'precio3', label: 'Precio 3', valor: precios.precio3, color: '#92400e', bgSelected: '#fef3c7' },
                                                        { tipo: 'precio4', label: 'Precio 4', valor: precios.precio4, color: '#9f1239', bgSelected: '#fce7f3' },
                                                        { tipo: 'precio5', label: 'Precio 5', valor: precios.precio5, color: '#0891b2', bgSelected: '#cffafe' },
                                                        { tipo: 'precio6', label: 'Precio 6', valor: precios.precio6, color: '#7e22ce', bgSelected: '#f3e8ff' }
                                                    ].filter(p => p.valor > 0);

                                                    return (
                                                        <div style={{
                                                            position: 'fixed',
                                                            top: showPriceSelector.position?.top ? `${showPriceSelector.position.top}px` : '50%',
                                                            left: showPriceSelector.position?.left ? `${showPriceSelector.position.left}px` : '50%',
                                                            transform: showPriceSelector.position ? 'none' : 'translate(-50%, -50%)',
                                                            background: 'white',
                                                            border: '1px solid #e5e7eb',
                                                            borderRadius: '8px',
                                                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                                            zIndex: 10000,
                                                            minWidth: '250px'
                                                        }}>
                                                            <div style={{
                                                                padding: '12px 14px',
                                                                borderBottom: '1px solid #e5e7eb',
                                                                background: '#f9fafb'
                                                            }}>
                                                                <div style={{
                                                                    fontSize: '14px',
                                                                    fontWeight: 600,
                                                                    color: '#374151',
                                                                    marginBottom: '4px',
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    maxWidth: '280px'
                                                                }}>
                                                                    {item.producto_nombre || item.nombre}
                                                                </div>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <span style={{ fontSize: '13px', color: '#6b7280' }}>Seleccionar precio</span>
                                                                    <span style={{ fontSize: '11px', color: '#9ca3af' }}>↑↓ Enter Esc</span>
                                                                </div>
                                                            </div>
                                                            {preciosConfig.map((precio, index) => (
                                                                <button
                                                                    key={precio.tipo}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        changePriceLevel(item.id, precio.valor, precio.tipo);
                                                                        setShowPriceSelector(null);
                                                                    }}
                                                                    style={{
                                                                        width: '100%',
                                                                        padding: '10px 12px',
                                                                        border: 'none',
                                                                        background: selectedPriceIndex === index ? '#3b82f6' :
                                                                            (showPriceSelector.currentPrice === precio.valor ? precio.bgSelected : 'white'),
                                                                        textAlign: 'left',
                                                                        cursor: 'pointer',
                                                                        fontSize: '16px',
                                                                        display: 'flex',
                                                                        justifyContent: 'space-between',
                                                                        alignItems: 'center',
                                                                        borderBottom: '1px solid #f3f4f6',
                                                                        outline: 'none'
                                                                    }}
                                                                    onMouseEnter={() => setSelectedPriceIndex(index)}
                                                                >
                                                                    <span style={{ color: selectedPriceIndex === index ? 'white' : precio.color, fontWeight: 500 }}>
                                                                        {precio.label}
                                                                    </span>
                                                                    <span style={{ fontWeight: 600, color: selectedPriceIndex === index ? 'white' : '#1f2937' }}>
                                                                        {formatMoney(precio.valor)}
                                                                    </span>
                                                                </button>
                                                            ))}
                                                            <button
                                                                onClick={() => setShowPriceSelector(null)}
                                                                style={{
                                                                    width: '100%',
                                                                    padding: '10px 14px',
                                                                    border: 'none',
                                                                    background: 'white',
                                                                    textAlign: 'center',
                                                                    cursor: 'pointer',
                                                                    fontSize: '15px',
                                                                    color: '#6b7280',
                                                                    fontWeight: 500
                                                                }}
                                                            >
                                                                Cancelar
                                                            </button>
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        </td>
                                        <td className="text-right font-semibold">
                                            {formatMoney(item.cantidad * item.precio_unitario)}
                                        </td>
                                        <td className="text-center">
                                            <button className="delete-btn-table" onClick={() => deleteFromCart(item.id)} title="Eliminar">
                                                <Trash2 size={18} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Footer */}
                <div className="sale-footer-table" style={{ display: 'flex', flexDirection: 'column', gap: '12px', justifyContent: 'flex-start', alignItems: 'stretch' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '20px', width: '100%' }}>
                        {/* Observaciones */}
                        <div style={{ flex: '1', maxWidth: '500px' }}>
                            <label style={{ display: 'block', fontSize: '16px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                                Instrucciones / Observaciones:
                            </label>
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                                <textarea
                                    value={observaciones}
                                    onChange={(e) => setObservaciones(e.target.value)}
                                    placeholder="Agregar instrucciones especiales para esta venta..."
                                    rows={3}
                                    style={{
                                        flex: 1,
                                        padding: '12px 14px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        fontSize: '15px',
                                        fontFamily: 'inherit',
                                        resize: 'vertical',
                                        minHeight: '90px',
                                        outline: 'none'
                                    }}
                                />
                                <button
                                    onClick={() => setShowVentasPausadasModal(true)}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '6px',
                                        padding: '12px 16px',
                                        minWidth: '90px',
                                        height: '90px',
                                        border: ventasPausadas.length > 0 ? '2px solid #f59e0b' : '1px solid #d1d5db',
                                        borderRadius: '8px',
                                        background: ventasPausadas.length > 0 ? '#fef3c7' : '#f9fafb',
                                        cursor: 'pointer',
                                        position: 'relative'
                                    }}
                                    title="Ver ventas en espera (F3)"
                                >
                                    <Layers size={24} color={ventasPausadas.length > 0 ? '#d97706' : '#6b7280'} />
                                    <span style={{ fontSize: '12px', fontWeight: 600, color: ventasPausadas.length > 0 ? '#92400e' : '#6b7280' }}>
                                        En Espera
                                    </span>
                                    {ventasPausadas.length > 0 && (
                                        <span style={{
                                            position: 'absolute',
                                            top: '-8px',
                                            right: '-8px',
                                            background: '#dc2626',
                                            color: 'white',
                                            borderRadius: '50%',
                                            width: '24px',
                                            height: '24px',
                                            fontSize: '13px',
                                            fontWeight: 700,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            {ventasPausadas.length}
                                        </span>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Total y botón */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '12px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                                <div className="total-display">
                                    <span className="total-label">Total:</span>
                                    <span className="total-amount">{formatMoney(totals.total)}</span>
                                </div>
                                {totals.tienePrecioMayoreo && comisionTarjeta > 0 && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <CreditCard size={14} color="#d97706" />
                                        <span style={{ fontSize: '12px', color: '#92400e' }}>Con tarjeta:</span>
                                        <span style={{ fontSize: '14px', color: '#b45309', fontWeight: 600 }}>
                                            {formatMoney(totals.totalConComision)}
                                        </span>
                                    </div>
                                )}
                            </div>
                            <button
                                className="btn-process-sale"
                                onClick={handleProcessSale}
                                disabled={loading || cart.length === 0}
                                title="Procesar Venta (F10)"
                            >
                                {loading ? (
                                    <span>Procesando...</span>
                                ) : (
                                    <>
                                        <ShoppingCart size={24} />
                                        <span>Procesar Venta</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Barra de atajos */}
                    <div style={{ textAlign: 'center', borderTop: '1px solid #e5e7eb', paddingTop: '0.75rem', paddingBottom: '0.25rem' }}>
                        <p style={{ margin: 0, fontSize: '0.9rem', color: '#000000', lineHeight: '1.5' }}>
                            <kbd>F1</kbd>: Buscar | <kbd>F2</kbd>: Cliente | <kbd>F3</kbd>: Espera | <kbd>F4</kbd>: Precio | <kbd>F5</kbd>: Pausar | <kbd>F6</kbd>: Devolución | <kbd>F7</kbd>: Retiro | <kbd>F8</kbd>: Reimprimir | <kbd>F9</kbd>: Nuevo | <kbd>F10</kbd>: Pagar | <kbd>F11</kbd>: Unidad | <kbd>F12</kbd>: Ingreso | <kbd>Ctrl+H</kbd>: Historial | <kbd>Alt</kbd>: Menú
                        </p>
                    </div>
                </div>
            </div>

            {/* Modal de cantidad */}
            <QuantityModal
                isOpen={!!showQuantityModal}
                onClose={() => {
                    setShowQuantityModal(null);
                    setEquivalenciasDisponibles([]);
                    setTimeout(() => searchInputRef.current?.focus(), 100);
                }}
                onConfirm={({ cantidad, calculatedPricing: pricing }) => {
                    if (!showQuantityModal) return;
                    addToCartWithQuantity(showQuantityModal.producto, cantidad, pricing, equivalenciasDisponibles);
                    setShowQuantityModal(null);
                    setEquivalenciasDisponibles([]);
                    setTimeout(() => searchInputRef.current?.focus(), 100);
                }}
                producto={showQuantityModal?.producto}
                initialQuantity={1}
                equivalencias={equivalenciasDisponibles}
                loadingEquivalencias={loadingEquivalencias}
                getCustomerPriceFromPresentacion={getCustomerPriceFromPresentacion}
                cliente_id={cliente?.id || null}
            />

            {/* Client Search Modal */}
            {showClientModal && (
                <Modal
                    isOpen={showClientModal}
                    onClose={() => {
                        setShowClientModal(false);
                        setClientSearch('');
                        setClientResults([]);
                        setShowClientResults(false);
                    }}
                    title="Seleccionar Cliente"
                    size="medium"
                >
                    <div className="wizard-step phone-step">
                        <div className="step-content">
                            <div className="phone-input-group" style={{ position: 'relative' }}>
                                <Input
                                    ref={clientSearchInputRef}
                                    type="text"
                                    placeholder="Teléfono (10 dígitos) o Nombre del cliente..."
                                    value={clientSearch}
                                    onChange={(e) => setClientSearch(e.target.value)}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            if (clientResults.length > 0 && clientResults[selectedClientIndex]) {
                                                selectClient(clientResults[selectedClientIndex]);
                                            }
                                        }
                                    }}
                                    className="phone-input-large"
                                    autoFocus
                                />

                                {/* Client search results dropdown */}
                                {showClientResults && clientResults.length > 0 && (
                                    <div className="search-results-dropdown" style={{ marginTop: '4px' }}>
                                        <div className="search-hint">
                                            Use ↑↓ para navegar, <kbd>Enter</kbd> para seleccionar
                                        </div>
                                        {clientResults.map((client, index) => (
                                            <div
                                                key={client.id}
                                                className={`search-result-item ${selectedClientIndex === index ? 'keyboard-focused' : ''}`}
                                                onClick={() => selectClient(client)}
                                            >
                                                <div className="result-info">
                                                    <span className="result-name">{client.nombre}</span>
                                                    {client.nombre_comercial && (
                                                        <span className="result-code">{client.nombre_comercial}</span>
                                                    )}
                                                    {client.telefonos && client.telefonos.length > 0 ? (
                                                        <span className="result-code">
                                                            📞 {client.telefonos.map(t => t.telefono || t).join(', ')}
                                                        </span>
                                                    ) : client.telefono && (
                                                        <span className="result-code">📞 {client.telefono}</span>
                                                    )}
                                                </div>
                                                <div className="result-price" style={{ fontSize: '16px', color: '#667eea' }}>
                                                    Seleccionar →
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {showClientResults && clientResults.length === 0 && clientSearch.length >= 2 && (
                                    <div className="search-results-dropdown" style={{ marginTop: '4px' }}>
                                        <div className="no-results">
                                            No se encontraron clientes
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="step-hint" style={{ marginTop: '16px' }}>
                                <p>Busca por nombre (2+ letras) o teléfono completo (10 dígitos)</p>
                                <p style={{ fontSize: '14px', marginTop: '4px', color: '#9ca3af' }}>
                                    Presiona <kbd>Enter</kbd> para buscar con menos dígitos
                                </p>
                            </div>
                        </div>

                        <div className="step-actions">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setShowClientModal(false);
                                    setClientSearch('');
                                    setClientResults([]);
                                    setShowClientResults(false);
                                }}
                            >
                                Cancelar
                            </Button>
                            <Button
                                variant="ghost"
                                onClick={() => {
                                    setCliente(null);
                                    setShowClientModal(false);
                                    showToast('Venta sin cliente específico', 'info');
                                }}
                            >
                                Sin Cliente
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Modal de Ventas Pausadas */}
            {showVentasPausadasModal && (
                <Modal
                    isOpen={showVentasPausadasModal}
                    onClose={() => setShowVentasPausadasModal(false)}
                    title={`Ventas en Espera (${ventasPausadas.length})`}
                    size="medium"
                >
                    <div style={{ padding: '8px 0' }}>
                        {ventasPausadas.length === 0 ? (
                            <div style={{
                                textAlign: 'center',
                                padding: '40px 20px',
                                color: '#6b7280'
                            }}>
                                <Layers size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                                <p style={{ fontSize: '16px', margin: 0 }}>No hay ventas en espera</p>
                                <p style={{ fontSize: '14px', marginTop: '8px' }}>
                                    Presiona <kbd style={{ background: '#e5e7eb', padding: '2px 6px', borderRadius: '4px' }}>F5</kbd> para pausar la venta actual
                                </p>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {ventasPausadas.map((venta, index) => {
                                    const isSelected = index === selectedVentaPausadaIndex;
                                    return (
                                        <div
                                            key={venta.id}
                                            onClick={() => setSelectedVentaPausadaIndex(index)}
                                            onDoubleClick={() => retomarVentaPausada(venta)}
                                            style={{
                                                border: isSelected ? '2px solid #059669' : '1px solid #e5e7eb',
                                                borderRadius: '8px',
                                                padding: '12px 16px',
                                                background: isSelected ? '#ecfdf5' : '#fafafa',
                                                cursor: 'pointer',
                                                transition: 'all 0.15s ease',
                                                boxShadow: isSelected ? '0 0 0 3px rgba(5, 150, 105, 0.15)' : 'none'
                                            }}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                                <div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                                        {isSelected && (
                                                            <span style={{
                                                                background: '#059669',
                                                                color: 'white',
                                                                padding: '2px 6px',
                                                                borderRadius: '4px',
                                                                fontSize: '11px',
                                                                fontWeight: 600
                                                            }}>
                                                                ▶
                                                            </span>
                                                        )}
                                                        <Clock size={14} color={isSelected ? '#059669' : '#6b7280'} />
                                                        <span style={{ fontSize: '13px', color: isSelected ? '#059669' : '#6b7280' }}>
                                                            {new Date(venta.fecha).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                                                        </span>
                                                        {venta.cliente && (
                                                            <span style={{
                                                                background: '#dbeafe',
                                                                color: '#1d4ed8',
                                                                padding: '2px 8px',
                                                                borderRadius: '4px',
                                                                fontSize: '12px',
                                                                fontWeight: 500
                                                            }}>
                                                                <User size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                                                                {venta.cliente.nombre}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div style={{ fontSize: '14px', color: '#374151' }}>
                                                        {venta.cart.length} producto{venta.cart.length !== 1 ? 's' : ''}
                                                    </div>
                                                </div>
                                                <div style={{ textAlign: 'right' }}>
                                                    <div style={{ fontSize: '18px', fontWeight: 700, color: '#059669' }}>
                                                        {formatMoney(venta.cart.reduce((sum, item) => sum + ((item.precio_unitario || 0) * (item.cantidad || 0)), 0))}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Lista de productos resumida */}
                                            <div style={{
                                                background: 'white',
                                                borderRadius: '4px',
                                                padding: '8px',
                                                marginBottom: '12px',
                                                maxHeight: '80px',
                                                overflowY: 'auto',
                                                fontSize: '13px',
                                                color: '#6b7280'
                                            }}>
                                                {venta.cart.slice(0, 3).map((item, idx) => (
                                                    <div key={idx} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <span>{item.cantidad}x {item.producto_nombre}</span>
                                                        <span>{formatMoney(item.precio_unitario * item.cantidad)}</span>
                                                    </div>
                                                ))}
                                                {venta.cart.length > 3 && (
                                                    <div style={{ fontStyle: 'italic', marginTop: '4px' }}>
                                                        ... y {venta.cart.length - 3} producto{venta.cart.length - 3 !== 1 ? 's' : ''} más
                                                    </div>
                                                )}
                                            </div>

                                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                                <Button
                                                    variant="ghost"
                                                    size="small"
                                                    onClick={(e) => { e.stopPropagation(); eliminarVentaPausada(venta.id); }}
                                                    style={{ color: '#dc2626' }}
                                                >
                                                    <Trash2 size={16} style={{ marginRight: '4px' }} />
                                                    Eliminar
                                                </Button>
                                                <Button
                                                    variant="primary"
                                                    size="small"
                                                    onClick={(e) => { e.stopPropagation(); retomarVentaPausada(venta); }}
                                                    style={{ background: '#059669' }}
                                                >
                                                    <Play size={16} style={{ marginRight: '4px' }} />
                                                    Retomar
                                                </Button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginTop: '20px',
                            paddingTop: '16px',
                            borderTop: '1px solid #e5e7eb'
                        }}>
                            <div style={{ fontSize: '12px', color: '#6b7280', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                <span>
                                    <kbd style={{ background: '#e5e7eb', padding: '2px 6px', borderRadius: '4px' }}>↑↓</kbd> Navegar
                                </span>
                                <span>
                                    <kbd style={{ background: '#e5e7eb', padding: '2px 6px', borderRadius: '4px' }}>Enter</kbd> Retomar
                                </span>
                                <span>
                                    <kbd style={{ background: '#e5e7eb', padding: '2px 6px', borderRadius: '4px' }}>Del</kbd> Eliminar
                                </span>
                                <span>
                                    <kbd style={{ background: '#e5e7eb', padding: '2px 6px', borderRadius: '4px' }}>Esc</kbd> Cerrar
                                </span>
                            </div>
                            <Button
                                variant="secondary"
                                onClick={() => setShowVentasPausadasModal(false)}
                            >
                                Cerrar
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Modal de selección de comanda */}
            {showComandaModal && ventaPendiente && (
                <Modal
                    title="Procesar Venta"
                    onClose={() => {
                        setShowComandaModal(false);
                        setVentaPendiente(null);
                    }}
                    size="medium"
                >
                    <div style={{ padding: '20px' }}>
                        <p style={{ marginBottom: '16px', color: '#374151', fontSize: '17px' }}>
                            ¿Dónde deseas imprimir la comanda? ({cart.length} producto{cart.length !== 1 ? 's' : ''})
                        </p>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
                            {/* Opción Local */}
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '16px',
                                    padding: '16px',
                                    border: '2px solid #10b981',
                                    borderRadius: '12px',
                                    background: '#ecfdf5',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    opacity: loading ? 0.6 : 1,
                                    transition: 'all 0.2s'
                                }}
                                onClick={() => !loading && procesarVentaConComanda(ventaPendiente, 'local')}
                            >
                                <div style={{
                                    width: '48px',
                                    height: '48px',
                                    borderRadius: '50%',
                                    background: '#d1fae5',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <Printer size={24} color="#10b981" />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 600, color: '#1f2937', fontSize: '17px', marginBottom: '4px' }}>
                                        Imprimir Local
                                    </div>
                                    <div style={{ fontSize: '15px', color: '#6b7280' }}>
                                        Impresora: <strong>{impresoraPredeterminada || 'Cargando...'}</strong>
                                    </div>
                                </div>
                            </div>

                            {/* Opción Almacenes */}
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '16px',
                                    padding: '16px',
                                    border: '2px solid #667eea',
                                    borderRadius: '12px',
                                    background: '#f5f3ff',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    opacity: loading ? 0.6 : 1,
                                    transition: 'all 0.2s'
                                }}
                                onClick={() => !loading && procesarVentaConComanda(ventaPendiente, 'almacenes')}
                            >
                                <div style={{
                                    width: '48px',
                                    height: '48px',
                                    borderRadius: '50%',
                                    background: '#e0e7ff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <Warehouse size={24} color="#667eea" />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 600, color: '#1f2937', fontSize: '17px', marginBottom: '4px' }}>
                                        Mandar a Almacenes
                                    </div>
                                    <div style={{ fontSize: '15px', color: '#6b7280' }}>
                                        Envía a las impresoras configuradas
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Botón sin comanda */}
                        <div style={{ textAlign: 'center', borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
                            <Button
                                variant="ghost"
                                onClick={() => procesarVentaConComanda(ventaPendiente, null)}
                                disabled={loading}
                                style={{ color: '#6b7280' }}
                            >
                                {loading ? 'Procesando...' : 'Procesar sin comanda'}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Modal de contraseña para precio libre */}
            {showPasswordPrecioLibreModal && (
                <Modal
                    title="Autorización Requerida"
                    onClose={cerrarPrecioLibre}
                    size="small"
                >
                    <div style={{ padding: '20px' }}>
                        <div style={{
                            textAlign: 'center',
                            marginBottom: '20px',
                            padding: '16px',
                            background: '#fef3c7',
                            borderRadius: '8px',
                            border: '1px solid #f59e0b'
                        }}>
                            <Edit3 size={32} color="#d97706" style={{ marginBottom: '8px' }} />
                            <p style={{ margin: 0, color: '#92400e', fontWeight: 600 }}>
                                Precio Libre
                            </p>
                            <p style={{ margin: '8px 0 0', color: '#a16207', fontSize: '14px' }}>
                                Ingresa la contraseña de un usuario autorizado
                            </p>
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#374151' }}>
                                Producto: {precioLibreData.productoNombre}
                            </label>
                        </div>

                        <Input
                            ref={passwordPrecioLibreInputRef}
                            type="password"
                            placeholder="Contraseña de autorización"
                            value={passwordPrecioLibre}
                            onChange={(e) => setPasswordPrecioLibre(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') handleVerificarPasswordPrecioLibre();
                            }}
                            disabled={verificandoPasswordPrecioLibre}
                        />

                        <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                            <Button
                                variant="secondary"
                                onClick={cerrarPrecioLibre}
                                disabled={verificandoPasswordPrecioLibre}
                                style={{ flex: 1 }}
                            >
                                Cancelar
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleVerificarPasswordPrecioLibre}
                                loading={verificandoPasswordPrecioLibre}
                                disabled={verificandoPasswordPrecioLibre || !passwordPrecioLibre}
                                style={{ flex: 1 }}
                            >
                                Verificar
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Modal de precio libre */}
            {showPrecioLibreModal && (
                <Modal
                    title="Capturar Precio Libre"
                    onClose={cerrarPrecioLibre}
                    size="small"
                >
                    <div style={{ padding: '20px' }}>
                        <div style={{
                            marginBottom: '20px',
                            padding: '16px',
                            background: '#f0fdf4',
                            borderRadius: '8px',
                            border: '1px solid #86efac'
                        }}>
                            <p style={{ margin: 0, fontWeight: 600, color: '#166534' }}>
                                {precioLibreData.productoNombre}
                            </p>
                            <p style={{ margin: '8px 0 0', color: '#15803d', fontSize: '14px' }}>
                                Precio mínimo (costo): <strong>{formatMoney(precioLibreData.precioCosto)}</strong>
                            </p>
                            {usuarioAutorizadorPrecioLibre && (
                                <p style={{ margin: '8px 0 0', color: '#059669', fontSize: '13px' }}>
                                    Autorizado por: {usuarioAutorizadorPrecioLibre}
                                </p>
                            )}
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#374151' }}>
                                Nuevo Precio:
                            </label>
                            <Input
                                ref={precioLibreInputRef}
                                type="number"
                                placeholder="0.00"
                                value={precioLibreData.precio}
                                onChange={(e) => setPrecioLibreData(prev => ({ ...prev, precio: e.target.value }))}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') handleAplicarPrecioLibre();
                                }}
                                min={precioLibreData.precioCosto}
                                step="0.01"
                                style={{ fontSize: '24px', textAlign: 'center', fontWeight: 600 }}
                            />
                            {precioLibreData.precio && parseFloat(precioLibreData.precio) < precioLibreData.precioCosto && (
                                <p style={{ color: '#dc2626', fontSize: '13px', marginTop: '8px' }}>
                                    El precio no puede ser menor al costo
                                </p>
                            )}
                        </div>

                        <div style={{ display: 'flex', gap: '12px' }}>
                            <Button
                                variant="secondary"
                                onClick={cerrarPrecioLibre}
                                style={{ flex: 1 }}
                            >
                                Cancelar
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleAplicarPrecioLibre}
                                disabled={!precioLibreData.precio || parseFloat(precioLibreData.precio) < precioLibreData.precioCosto}
                                style={{ flex: 1, background: '#059669' }}
                            >
                                Aplicar Precio
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Modal de catálogo */}
            <ProductsCatalogModal
                isOpen={showProductsModal}
                onClose={handleCloseProductsModal}
                onSelectProduct={(producto) => {
                    handleAddToCart(producto);
                    setShowProductsModal(false);
                }}
            />

            {/* Payment Confirmation Modal */}
            {showPaymentConfirmModal && (() => {
                const totalPagado = pagosDetalle.reduce((sum, pago) => sum + (parseFloat(pago.monto) || 0), 0);
                const metodosConComision = ['tarjeta_credito', 'transferencia'];
                const tieneTarjetaOTransferencia = pagosDetalle.some(p => metodosConComision.includes(p.metodo));
                const aplicaComision = totals.tienePrecioMayoreo && comisionTarjeta > 0 && tieneTarjetaOTransferencia;
                const totalFinal = aplicaComision ? totals.totalConComision : totals.total;
                const restante = totalFinal - totalPagado;

                return (
                    <Modal
                        title="Confirmar Pago"
                        onClose={() => {
                            setShowPaymentConfirmModal(false);
                            setPagosDetalle([]);
                        }}
                        size="xlarge"
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '24px' }}>
                            {/* Layout de dos columnas */}
                            <div style={{ display: 'grid', gridTemplateColumns: '420px 1fr', gap: '30px' }}>
                                {/* Columna Izquierda - Métodos de Pago */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <label style={{ fontSize: '17px', fontWeight: 600, color: '#374151' }}>
                                            Métodos de Pago:
                                        </label>

                                        {/* Efectivo */}
                                        {(() => {
                                            const pagoIndex = pagosDetalle.findIndex(p => p.metodo === 'efectivo');
                                            const estaSeleccionado = pagoIndex !== -1;

                                            return (
                                                <div
                                                    onClick={() => !estaSeleccionado && agregarMetodoPago('efectivo')}
                                                    style={{
                                                        padding: '12px 16px',
                                                        border: estaSeleccionado ? '2px solid #10b981' : '1px solid #e5e7eb',
                                                        borderRadius: '6px',
                                                        background: estaSeleccionado ? '#f0fdf4' : 'white',
                                                        transition: 'all 0.15s',
                                                        cursor: estaSeleccionado ? 'default' : 'pointer'
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                        <span style={{ fontSize: '20px' }}>💵</span>
                                                        <span style={{ fontSize: '17px', fontWeight: 600, color: '#374151', flex: '0 0 120px' }}>Efectivo</span>

                                                        {estaSeleccionado ? (
                                                            <>
                                                                <span style={{ fontSize: '17px', color: '#6b7280' }}>$</span>
                                                                <input
                                                                    type="number"
                                                                    value={pagosDetalle[pagoIndex].monto || ''}
                                                                    onChange={(e) => actualizarMonto(pagoIndex, parseFloat(e.target.value) || 0)}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    step="0.01"
                                                                    min="0"
                                                                    style={{
                                                                        width: '140px',
                                                                        padding: '8px 12px',
                                                                        border: '1px solid #d1d5db',
                                                                        borderRadius: '4px',
                                                                        fontSize: '17px',
                                                                        fontWeight: 600,
                                                                        boxSizing: 'border-box',
                                                                        textAlign: 'right'
                                                                    }}
                                                                    onFocus={(e) => e.target.select()}
                                                                    autoFocus
                                                                />
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        eliminarMetodoPago(pagoIndex);
                                                                    }}
                                                                    style={{
                                                                        padding: '6px',
                                                                        background: '#fee2e2',
                                                                        border: '1px solid #fca5a5',
                                                                        borderRadius: '4px',
                                                                        cursor: 'pointer',
                                                                        color: '#991b1b',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        flexShrink: 0
                                                                    }}
                                                                    title="Eliminar"
                                                                >
                                                                    <X size={16} />
                                                                </button>
                                                            </>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        {/* Tarjeta Crédito */}
                                        {(() => {
                                            const pagoIndex = pagosDetalle.findIndex(p => p.metodo === 'tarjeta_credito');
                                            const estaSeleccionado = pagoIndex !== -1;

                                            return (
                                                <div
                                                    onClick={() => !estaSeleccionado && agregarMetodoPago('tarjeta_credito')}
                                                    style={{
                                                        padding: '12px 16px',
                                                        border: estaSeleccionado ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                                                        borderRadius: '6px',
                                                        background: estaSeleccionado ? '#eff6ff' : 'white',
                                                        transition: 'all 0.15s',
                                                        cursor: estaSeleccionado ? 'default' : 'pointer'
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                        <span style={{ fontSize: '20px' }}>💳</span>
                                                        <span style={{ fontSize: '17px', fontWeight: 600, color: '#374151', flex: '0 0 140px' }}>T. Crédito</span>

                                                        {estaSeleccionado ? (
                                                            <>
                                                                <span style={{ fontSize: '17px', color: '#6b7280' }}>$</span>
                                                                <input
                                                                    type="number"
                                                                    value={pagosDetalle[pagoIndex].monto || ''}
                                                                    onChange={(e) => actualizarMonto(pagoIndex, parseFloat(e.target.value) || 0)}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    step="0.01"
                                                                    min="0"
                                                                    style={{
                                                                        width: '140px',
                                                                        padding: '8px 12px',
                                                                        border: '1px solid #d1d5db',
                                                                        borderRadius: '4px',
                                                                        fontSize: '17px',
                                                                        fontWeight: 600,
                                                                        boxSizing: 'border-box',
                                                                        textAlign: 'right'
                                                                    }}
                                                                    onFocus={(e) => e.target.select()}
                                                                    autoFocus
                                                                />
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        eliminarMetodoPago(pagoIndex);
                                                                    }}
                                                                    style={{
                                                                        padding: '6px',
                                                                        background: '#fee2e2',
                                                                        border: '1px solid #fca5a5',
                                                                        borderRadius: '4px',
                                                                        cursor: 'pointer',
                                                                        color: '#991b1b',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        flexShrink: 0
                                                                    }}
                                                                    title="Eliminar"
                                                                >
                                                                    <X size={16} />
                                                                </button>
                                                            </>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        {/* Tarjeta Débito */}
                                        {(() => {
                                            const pagoIndex = pagosDetalle.findIndex(p => p.metodo === 'tarjeta_debito');
                                            const estaSeleccionado = pagoIndex !== -1;

                                            return (
                                                <div
                                                    onClick={() => !estaSeleccionado && agregarMetodoPago('tarjeta_debito')}
                                                    style={{
                                                        padding: '12px 16px',
                                                        border: estaSeleccionado ? '2px solid #06b6d4' : '1px solid #e5e7eb',
                                                        borderRadius: '6px',
                                                        background: estaSeleccionado ? '#ecfeff' : 'white',
                                                        transition: 'all 0.15s',
                                                        cursor: estaSeleccionado ? 'default' : 'pointer'
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                        <span style={{ fontSize: '20px' }}>💳</span>
                                                        <span style={{ fontSize: '17px', fontWeight: 600, color: '#374151', flex: '0 0 140px' }}>T. Débito</span>

                                                        {estaSeleccionado ? (
                                                            <>
                                                                <span style={{ fontSize: '17px', color: '#6b7280' }}>$</span>
                                                                <input
                                                                    type="number"
                                                                    value={pagosDetalle[pagoIndex].monto || ''}
                                                                    onChange={(e) => actualizarMonto(pagoIndex, parseFloat(e.target.value) || 0)}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    step="0.01"
                                                                    min="0"
                                                                    style={{
                                                                        width: '140px',
                                                                        padding: '8px 12px',
                                                                        border: '1px solid #d1d5db',
                                                                        borderRadius: '4px',
                                                                        fontSize: '17px',
                                                                        fontWeight: 600,
                                                                        boxSizing: 'border-box',
                                                                        textAlign: 'right'
                                                                    }}
                                                                    onFocus={(e) => e.target.select()}
                                                                    autoFocus
                                                                />
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        eliminarMetodoPago(pagoIndex);
                                                                    }}
                                                                    style={{
                                                                        padding: '6px',
                                                                        background: '#fee2e2',
                                                                        border: '1px solid #fca5a5',
                                                                        borderRadius: '4px',
                                                                        cursor: 'pointer',
                                                                        color: '#991b1b',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        flexShrink: 0
                                                                    }}
                                                                    title="Eliminar"
                                                                >
                                                                    <X size={16} />
                                                                </button>
                                                            </>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        {/* Transferencia */}
                                        {(() => {
                                            const pagoIndex = pagosDetalle.findIndex(p => p.metodo === 'transferencia');
                                            const estaSeleccionado = pagoIndex !== -1;

                                            return (
                                                <div
                                                    onClick={() => !estaSeleccionado && agregarMetodoPago('transferencia')}
                                                    style={{
                                                        padding: '12px 16px',
                                                        border: estaSeleccionado ? '2px solid #8b5cf6' : '1px solid #e5e7eb',
                                                        borderRadius: '6px',
                                                        background: estaSeleccionado ? '#f5f3ff' : 'white',
                                                        transition: 'all 0.15s',
                                                        cursor: estaSeleccionado ? 'default' : 'pointer'
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                        <span style={{ fontSize: '20px' }}>🏦</span>
                                                        <span style={{ fontSize: '17px', fontWeight: 600, color: '#374151', flex: '0 0 120px' }}>Transferencia</span>

                                                        {estaSeleccionado ? (
                                                            <>
                                                                <span style={{ fontSize: '17px', color: '#6b7280' }}>$</span>
                                                                <input
                                                                    type="number"
                                                                    value={pagosDetalle[pagoIndex].monto || ''}
                                                                    onChange={(e) => actualizarMonto(pagoIndex, parseFloat(e.target.value) || 0)}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    step="0.01"
                                                                    min="0"
                                                                    style={{
                                                                        width: '140px',
                                                                        padding: '8px 12px',
                                                                        border: '1px solid #d1d5db',
                                                                        borderRadius: '4px',
                                                                        fontSize: '17px',
                                                                        fontWeight: 600,
                                                                        boxSizing: 'border-box',
                                                                        textAlign: 'right'
                                                                    }}
                                                                    onFocus={(e) => e.target.select()}
                                                                    autoFocus
                                                                />
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        eliminarMetodoPago(pagoIndex);
                                                                    }}
                                                                    style={{
                                                                        padding: '6px',
                                                                        background: '#fee2e2',
                                                                        border: '1px solid #fca5a5',
                                                                        borderRadius: '4px',
                                                                        cursor: 'pointer',
                                                                        color: '#991b1b',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        flexShrink: 0
                                                                    }}
                                                                    title="Eliminar"
                                                                >
                                                                    <X size={16} />
                                                                </button>
                                                            </>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>

                                {/* Columna Derecha - Resumen de Totales */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(3, 1fr)',
                                        gap: '12px'
                                    }}>
                                        <div style={{
                                            padding: '16px',
                                            background: aplicaComision ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' : '#f0fdf4',
                                            borderRadius: '8px',
                                            border: aplicaComision ? '2px solid #f59e0b' : '2px solid #10b981'
                                        }}>
                                            <div style={{ fontSize: '15px', color: '#6b7280', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                Total
                                                {aplicaComision && <CreditCard size={14} color="#d97706" />}
                                            </div>
                                            <div style={{ fontSize: '28px', fontWeight: 700, color: aplicaComision ? '#b45309' : '#10b981' }}>
                                                {formatMoney(totalFinal)}
                                            </div>
                                            {aplicaComision && (
                                                <div style={{ fontSize: '11px', color: '#92400e', marginTop: '4px' }}>
                                                    +{comisionTarjeta}% comisión
                                                </div>
                                            )}
                                        </div>
                                        <div style={{
                                            padding: '16px',
                                            background: '#eff6ff',
                                            borderRadius: '8px',
                                            border: '2px solid #3b82f6'
                                        }}>
                                            <div style={{ fontSize: '15px', color: '#6b7280', marginBottom: '4px' }}>Pagado</div>
                                            <div style={{ fontSize: '28px', fontWeight: 700, color: '#3b82f6' }}>{formatMoney(totalPagado)}</div>
                                        </div>
                                        <div style={{
                                            padding: '16px',
                                            background: Math.abs(restante) > 0.01 ? '#fef3c7' : '#f0fdf4',
                                            borderRadius: '8px',
                                            border: `2px solid ${Math.abs(restante) > 0.01 ? '#f59e0b' : '#10b981'}`
                                        }}>
                                            <div style={{ fontSize: '15px', color: '#6b7280', marginBottom: '4px' }}>Restante</div>
                                            <div style={{ fontSize: '28px', fontWeight: 700, color: Math.abs(restante) > 0.01 ? '#f59e0b' : '#10b981' }}>{formatMoney(Math.abs(restante) <= 0.01 ? 0 : restante)}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Botones de acción */}
                            <div style={{
                                display: 'flex',
                                gap: '12px',
                                paddingTop: '12px',
                                borderTop: '1px solid #e5e7eb'
                            }}>
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        setShowPaymentConfirmModal(false);
                                        setPagosDetalle([]);
                                    }}
                                    disabled={loading}
                                    style={{ flex: 1 }}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    onClick={confirmarPago}
                                    disabled={loading || Math.abs(restante) > 0.01}
                                    style={{
                                        flex: 1,
                                        background: Math.abs(restante) <= 0.01 ? '#10b981' : '#9ca3af',
                                        color: 'white',
                                        fontWeight: 600,
                                        cursor: Math.abs(restante) > 0.01 ? 'not-allowed' : 'pointer'
                                    }}
                                    title={Math.abs(restante) > 0.01 ? 'El pago debe ser exacto' : ''}
                                >
                                    {loading ? 'Procesando...' : 'Confirmar Venta'}
                                </Button>
                            </div>

                            {/* Guía de atajos de teclado al fondo */}
                            <div>
                                <p style={{
                                    margin: 0,
                                    fontSize: '14px',
                                    color: '#6b7280',
                                    lineHeight: '1.6',
                                    textAlign: 'center'
                                }}>
                                    <kbd>E</kbd> Efectivo | <kbd>C</kbd> T.Crédito | <kbd>D</kbd> T.Débito | <kbd>T</kbd> Transferencia |
                                    {' '}<kbd>Enter</kbd> Confirmar | <kbd>Esc</kbd> Cancelar
                                </p>
                            </div>
                        </div>
                    </Modal>
                );
            })()}

            {/* Menú Lateral Rápido (Alt) */}
            <div
                className={`quick-menu-panel ${showQuickMenu ? 'open' : ''}`}
                style={{
                    position: 'fixed',
                    top: 0,
                    right: showQuickMenu ? 0 : '-320px',
                    width: '320px',
                    height: '100vh',
                    background: 'white',
                    boxShadow: showQuickMenu ? '-4px 0 20px rgba(0, 0, 0, 0.15)' : 'none',
                    zIndex: 9999,
                    transition: 'right 0.3s ease-in-out',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}
            >
                {/* Header del menú */}
                <div style={{
                    padding: '20px',
                    borderBottom: '1px solid #e5e7eb',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Menu size={24} />
                            <span style={{ fontSize: '18px', fontWeight: 600 }}>Acciones Rápidas</span>
                        </div>
                        <button
                            onClick={() => setShowQuickMenu(false)}
                            style={{
                                background: 'rgba(255,255,255,0.2)',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '8px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white'
                            }}
                        >
                            <X size={20} />
                        </button>
                    </div>
                    <p style={{ margin: '8px 0 0', fontSize: '13px', opacity: 0.9 }}>
                        Presiona <kbd style={{ background: 'rgba(255,255,255,0.2)', padding: '2px 6px', borderRadius: '4px' }}>Alt</kbd> para cerrar
                    </p>
                </div>

                {/* Contenido del menú */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
                    {/* Sección: Resumen de Caja */}
                    {(() => {
                        const efectivoExcedido = alertaEfectivoMaximo > 0 && totalesCaja.total_efectivo > alertaEfectivoMaximo;
                        return (
                            <div style={{
                                marginBottom: '20px',
                                padding: '16px',
                                background: efectivoExcedido
                                    ? 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)'
                                    : 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                                borderRadius: '12px',
                                border: efectivoExcedido ? '2px solid #f87171' : '1px solid #86efac',
                                position: 'relative'
                            }}>
                                {/* Alerta de efectivo excedido */}
                                {efectivoExcedido && (
                                    <div style={{
                                        background: '#dc2626',
                                        color: 'white',
                                        padding: '8px 12px',
                                        borderRadius: '8px',
                                        marginBottom: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        animation: 'pulse 2s infinite'
                                    }}>
                                        <AlertTriangle size={18} />
                                        <span>EFECTIVO EXCEDIDO - Realizar Retiro</span>
                                    </div>
                                )}

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                    <h3 style={{
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        color: efectivoExcedido ? '#991b1b' : '#166534',
                                        margin: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}>
                                        <DollarSign size={18} />
                                        Ventas de esta Caja
                                    </h3>
                                    <button
                                        onClick={cargarTotalesCaja}
                                        disabled={totalesCaja.loading}
                                        style={{
                                            background: 'white',
                                            border: efectivoExcedido ? '1px solid #f87171' : '1px solid #86efac',
                                            borderRadius: '6px',
                                            padding: '4px 8px',
                                            cursor: totalesCaja.loading ? 'not-allowed' : 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            fontSize: '12px',
                                            color: efectivoExcedido ? '#991b1b' : '#166534'
                                        }}
                                        title="Actualizar"
                                    >
                                        <RefreshCw size={14} className={totalesCaja.loading ? 'spin' : ''} style={{
                                            animation: totalesCaja.loading ? 'spin 1s linear infinite' : 'none'
                                        }} />
                                    </button>
                                </div>

                                {totalesCaja.loading ? (
                                    <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                                        Cargando...
                                    </div>
                                ) : (
                                    <>
                                        {/* Total Efectivo - Destacado */}
                                        <div style={{
                                            background: 'white',
                                            borderRadius: '8px',
                                            padding: '12px',
                                            marginBottom: '12px',
                                            textAlign: 'center',
                                            border: efectivoExcedido ? '2px solid #f87171' : 'none'
                                        }}>
                                            <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>
                                                Efectivo desde último corte
                                            </div>
                                            <div style={{
                                                fontSize: '32px',
                                                fontWeight: 700,
                                                color: efectivoExcedido ? '#dc2626' : '#16a34a',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '4px'
                                            }}>
                                                <Banknote size={28} />
                                                {formatMoney(totalesCaja.total_efectivo)}
                                            </div>
                                            {alertaEfectivoMaximo > 0 && (
                                                <div style={{
                                                    fontSize: '12px',
                                                    color: efectivoExcedido ? '#dc2626' : '#6b7280',
                                                    marginTop: '4px'
                                                }}>
                                                    Límite: {formatMoney(alertaEfectivoMaximo)}
                                                    {efectivoExcedido && (
                                                        <span style={{ fontWeight: 600 }}>
                                                            {' '}(+{formatMoney(totalesCaja.total_efectivo - alertaEfectivoMaximo)})
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* Otros métodos de pago */}
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                            <div style={{
                                                background: 'white',
                                                borderRadius: '6px',
                                                padding: '10px',
                                                textAlign: 'center'
                                            }}>
                                                <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                                    <CreditCard size={12} /> Tarjeta
                                                </div>
                                                <div style={{ fontSize: '16px', fontWeight: 600, color: '#3b82f6' }}>
                                                    {formatMoney(totalesCaja.total_tarjeta)}
                                                </div>
                                            </div>
                                            <div style={{
                                                background: 'white',
                                                borderRadius: '6px',
                                                padding: '10px',
                                                textAlign: 'center'
                                            }}>
                                                <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '2px' }}>
                                                    Transferencia
                                                </div>
                                                <div style={{ fontSize: '16px', fontWeight: 600, color: '#8b5cf6' }}>
                                                    {formatMoney(totalesCaja.total_transferencia)}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Ingresos realizados */}
                                        {totalesCaja.total_ingresos > 0 && (
                                            <div style={{
                                                marginTop: '12px',
                                                padding: '8px 10px',
                                                background: '#ecfdf5',
                                                borderRadius: '6px',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                fontSize: '12px'
                                            }}>
                                                <span style={{ color: '#047857', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <DollarSign size={14} />
                                                    {totalesCaja.cantidad_ingresos} ingreso{totalesCaja.cantidad_ingresos !== 1 ? 's' : ''}
                                                </span>
                                                <span style={{ fontWeight: 600, color: '#059669' }}>
                                                    +{formatMoney(totalesCaja.total_ingresos)}
                                                </span>
                                            </div>
                                        )}

                                        {/* Retiros realizados */}
                                        {totalesCaja.total_retiros > 0 && (
                                            <div style={{
                                                marginTop: '12px',
                                                padding: '8px 10px',
                                                background: '#fef3c7',
                                                borderRadius: '6px',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                fontSize: '12px'
                                            }}>
                                                <span style={{ color: '#92400e', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <ArrowUpFromLine size={14} />
                                                    {totalesCaja.cantidad_retiros} retiro{totalesCaja.cantidad_retiros !== 1 ? 's' : ''}
                                                </span>
                                                <span style={{ fontWeight: 600, color: '#b45309' }}>
                                                    -{formatMoney(totalesCaja.total_retiros)}
                                                </span>
                                            </div>
                                        )}

                                        {/* Total general y ventas */}
                                        <div style={{
                                            marginTop: '12px',
                                            paddingTop: '12px',
                                            borderTop: efectivoExcedido ? '1px dashed #f87171' : '1px dashed #86efac',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            fontSize: '13px'
                                        }}>
                                            <span style={{ color: '#6b7280' }}>
                                                {totalesCaja.cantidad_ventas} venta{totalesCaja.cantidad_ventas !== 1 ? 's' : ''}
                                            </span>
                                            <span style={{ fontWeight: 600, color: '#374151' }}>
                                                Total: {formatMoney(totalesCaja.total_ventas)}
                                            </span>
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })()}

                    {/* Sección: Acciones */}
                    <div style={{ marginBottom: '24px' }}>
                        <h3 style={{
                            fontSize: '13px',
                            fontWeight: 600,
                            color: '#6b7280',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            marginBottom: '12px'
                        }}>
                            Acciones
                        </h3>

                        <button
                            onClick={() => {
                                setShowPasswordModal(true);
                                setShowQuickMenu(false);
                            }}
                            disabled={totalesCaja.total_efectivo <= 0}
                            style={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '14px 16px',
                                background: totalesCaja.total_efectivo <= 0 ? '#f9fafb' : '#fef3c7',
                                border: '1px solid',
                                borderColor: totalesCaja.total_efectivo <= 0 ? '#e5e7eb' : '#fcd34d',
                                borderRadius: '8px',
                                cursor: totalesCaja.total_efectivo <= 0 ? 'not-allowed' : 'pointer',
                                marginBottom: '8px',
                                transition: 'all 0.15s',
                                opacity: totalesCaja.total_efectivo <= 0 ? 0.5 : 1
                            }}
                        >
                            <ArrowUpFromLine size={20} color={totalesCaja.total_efectivo <= 0 ? '#9ca3af' : '#d97706'} />
                            <div style={{ textAlign: 'left' }}>
                                <div style={{ fontWeight: 600, color: totalesCaja.total_efectivo <= 0 ? '#9ca3af' : '#b45309' }}>Retiro de Efectivo</div>
                                <div style={{ fontSize: '13px', color: '#6b7280' }}>Retirar dinero de caja (F7)</div>
                            </div>
                        </button>

                        <button
                            onClick={() => {
                                setShowPasswordIngresoModal(true);
                                setShowQuickMenu(false);
                                setTimeout(() => passwordIngresoInputRef.current?.focus(), 100);
                            }}
                            style={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '14px 16px',
                                background: '#ecfdf5',
                                border: '1px solid #6ee7b7',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                marginBottom: '8px',
                                transition: 'all 0.15s'
                            }}
                        >
                            <DollarSign size={20} color="#059669" />
                            <div style={{ textAlign: 'left' }}>
                                <div style={{ fontWeight: 600, color: '#047857' }}>Ingreso de Efectivo</div>
                                <div style={{ fontSize: '13px', color: '#6b7280' }}>Agregar fondo o cambio (F12)</div>
                            </div>
                        </button>

                        <button
                            onClick={handleAbrirReimprimirModal}
                            style={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '14px 16px',
                                background: '#fefce8',
                                border: '1px solid #fde047',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                marginBottom: '8px',
                                transition: 'all 0.15s'
                            }}
                        >
                            <FileText size={20} color="#ca8a04" />
                            <div style={{ textAlign: 'left' }}>
                                <div style={{ fontWeight: 600, color: '#a16207' }}>Reimprimir Ticket</div>
                                <div style={{ fontSize: '13px', color: '#6b7280' }}>Seleccionar ticket a reimprimir (F8)</div>
                            </div>
                        </button>

                        <button
                            onClick={() => {
                                setView('devoluciones');
                                setShowQuickMenu(false);
                            }}
                            style={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '14px 16px',
                                background: '#fef2f2',
                                border: '1px solid #fca5a5',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                marginBottom: '8px',
                                transition: 'all 0.15s'
                            }}
                        >
                            <RotateCcw size={20} color="#dc2626" />
                            <div style={{ textAlign: 'left' }}>
                                <div style={{ fontWeight: 600, color: '#b91c1c' }}>Devolución</div>
                                <div style={{ fontSize: '13px', color: '#6b7280' }}>Devolver artículos de una venta (F6)</div>
                            </div>
                        </button>
                    </div>
                </div>

                {/* Footer del menú */}
                <div style={{
                    padding: '16px 20px',
                    borderTop: '1px solid #e5e7eb',
                    background: '#f9fafb'
                }}>
                    <div style={{ fontSize: '13px', color: '#6b7280', textAlign: 'center' }}>
                        Usuario: <strong>{user?.usuario || user?.nombre || 'Sin usuario'}</strong>
                    </div>
                </div>
            </div>

            {/* Overlay cuando el menú está abierto */}
            {showQuickMenu && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.3)',
                        zIndex: 9998,
                        transition: 'opacity 0.3s'
                    }}
                    onClick={() => setShowQuickMenu(false)}
                />
            )}

            {/* Modal de contraseña para retiro */}
            {showPasswordModal && (
                <Modal title="Autorización Requerida" onClose={() => { setShowPasswordModal(false); setPasswordRetiro(''); }} size="small">
                    <div style={{ padding: '24px' }}>
                        <p style={{ marginBottom: '16px', color: '#374151' }}>
                            Ingresa tu contraseña para autorizar el retiro de efectivo:
                        </p>
                        <Input
                            ref={passwordInputRef}
                            type="password"
                            placeholder="Contraseña"
                            value={passwordRetiro}
                            onChange={(e) => setPasswordRetiro(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleVerificarPasswordRetiro()}
                            autoFocus
                        />
                        <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                            <Button variant="outline" onClick={() => { setShowPasswordModal(false); setPasswordRetiro(''); }} style={{ flex: 1 }}>
                                Cancelar
                            </Button>
                            <Button onClick={handleVerificarPasswordRetiro} disabled={verificandoPassword} style={{ flex: 1 }}>
                                {verificandoPassword ? 'Verificando...' : 'Continuar'}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Modal de retiro de efectivo */}
            {showRetiroModal && (
                <Modal title="Retiro de Efectivo" onClose={() => { setShowRetiroModal(false); setRetiroData({ monto: '', motivo: '' }); }} size="small">
                    <div style={{ padding: '24px' }}>
                        <div style={{ marginBottom: '16px', padding: '12px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #86efac' }}>
                            <div style={{ fontSize: '13px', color: '#16a34a' }}>Efectivo disponible en caja:</div>
                            <div style={{ fontSize: '24px', fontWeight: 700, color: '#15803d' }}>{formatMoney(totalesCaja.total_efectivo)}</div>
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, color: '#374151' }}>Monto a retirar:</label>
                            <Input
                                type="number"
                                placeholder="0.00"
                                value={retiroData.monto}
                                onChange={(e) => setRetiroData({ ...retiroData, monto: e.target.value })}
                                step="0.01"
                                min="0"
                                autoFocus
                            />
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, color: '#374151' }}>Motivo (opcional):</label>
                            <Input
                                type="text"
                                placeholder="Ej: Pago a proveedor"
                                value={retiroData.motivo}
                                onChange={(e) => setRetiroData({ ...retiroData, motivo: e.target.value })}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <Button variant="outline" onClick={() => { setShowRetiroModal(false); setRetiroData({ monto: '', motivo: '' }); }} style={{ flex: 1 }}>
                                Cancelar
                            </Button>
                            <Button onClick={handleRetiroEfectivo} disabled={savingRetiro} style={{ flex: 1 }}>
                                {savingRetiro ? 'Procesando...' : 'Confirmar Retiro'}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Modal de contraseña para ingreso */}
            {showPasswordIngresoModal && (
                <Modal title="Autorización Requerida" onClose={() => { setShowPasswordIngresoModal(false); setPasswordIngreso(''); }} size="small">
                    <div style={{ padding: '24px' }}>
                        <p style={{ marginBottom: '16px', color: '#374151' }}>
                            Ingresa tu contraseña para autorizar el ingreso de efectivo:
                        </p>
                        <Input
                            ref={passwordIngresoInputRef}
                            type="password"
                            placeholder="Contraseña"
                            value={passwordIngreso}
                            onChange={(e) => setPasswordIngreso(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleVerificarPasswordIngreso()}
                            autoFocus
                        />
                        <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                            <Button variant="outline" onClick={() => { setShowPasswordIngresoModal(false); setPasswordIngreso(''); }} style={{ flex: 1 }}>
                                Cancelar
                            </Button>
                            <Button onClick={handleVerificarPasswordIngreso} disabled={verificandoPasswordIngreso} style={{ flex: 1 }}>
                                {verificandoPasswordIngreso ? 'Verificando...' : 'Continuar'}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Modal de ingreso de efectivo */}
            {showIngresoModal && (
                <Modal title="Ingreso de Efectivo" onClose={() => { setShowIngresoModal(false); setIngresoData({ monto: '', concepto: 'fondo_caja', descripcion: '' }); }} size="small">
                    <div style={{ padding: '24px' }}>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, color: '#374151' }}>Concepto:</label>
                            <select
                                value={ingresoData.concepto}
                                onChange={(e) => setIngresoData({ ...ingresoData, concepto: e.target.value })}
                                style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '16px' }}
                            >
                                <option value="fondo_caja">Fondo de Caja</option>
                                <option value="cambio">Cambio</option>
                                <option value="otro">Otro</option>
                            </select>
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, color: '#374151' }}>Monto:</label>
                            <Input
                                type="number"
                                placeholder="0.00"
                                value={ingresoData.monto}
                                onChange={(e) => setIngresoData({ ...ingresoData, monto: e.target.value })}
                                step="0.01"
                                min="0"
                                autoFocus
                            />
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, color: '#374151' }}>Descripción (opcional):</label>
                            <Input
                                type="text"
                                placeholder="Descripción adicional"
                                value={ingresoData.descripcion}
                                onChange={(e) => setIngresoData({ ...ingresoData, descripcion: e.target.value })}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <Button variant="outline" onClick={() => { setShowIngresoModal(false); setIngresoData({ monto: '', concepto: 'fondo_caja', descripcion: '' }); }} style={{ flex: 1 }}>
                                Cancelar
                            </Button>
                            <Button onClick={handleIngresoEfectivo} disabled={savingIngreso} style={{ flex: 1 }}>
                                {savingIngreso ? 'Procesando...' : 'Confirmar Ingreso'}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Modal de reimprimir ticket */}
            {showReimprimirModal && (
                <Modal title="Reimprimir Ticket" onClose={() => setShowReimprimirModal(false)} size="large">
                    <div style={{ padding: '24px' }}>
                        {loadingVentas ? (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                                Cargando ventas...
                            </div>
                        ) : ventasParaReimprimir.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                                No hay ventas disponibles para reimprimir
                            </div>
                        ) : (
                            <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                                {ventasParaReimprimir.map(venta => (
                                    <div key={venta.id} style={{
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                        padding: '16px', borderBottom: '1px solid #e5e7eb'
                                    }}>
                                        <div>
                                            <div style={{ fontWeight: 600, color: '#1f2937' }}>{venta.folio}</div>
                                            <div style={{ fontSize: '13px', color: '#6b7280' }}>
                                                {new Date(venta.fecha_venta || venta.created_at).toLocaleString()}
                                            </div>
                                            <div style={{ fontSize: '15px', color: '#16a34a', fontWeight: 600 }}>
                                                {formatMoney(venta.total)}
                                            </div>
                                        </div>
                                        <Button
                                            onClick={() => handleReimprimirTicket(venta)}
                                            disabled={reimprimiendo === venta.id}
                                            size="small"
                                        >
                                            {reimprimiendo === venta.id ? 'Imprimiendo...' : 'Reimprimir'}
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </Modal>
            )}

            {/* Modal de historial producto-cliente (Ctrl+H) */}
            {showHistorialModal && (
                <Modal
                    title="Historial de Ventas"
                    onClose={() => setShowHistorialModal(false)}
                    size="medium"
                >
                    <div style={{ padding: '24px' }}>
                        <div style={{
                            marginBottom: '16px',
                            padding: '12px 16px',
                            background: '#eff6ff',
                            borderRadius: '8px',
                            border: '1px solid #bfdbfe'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                <History size={18} color="#2563eb" />
                                <span style={{ fontWeight: 600, color: '#1e40af' }}>{historialProductoNombre}</span>
                            </div>
                            <div style={{ fontSize: '13px', color: '#3b82f6' }}>
                                Cliente: <strong>{cliente?.nombre}</strong> — Últimas 5 ventas
                            </div>
                        </div>

                        {/* Costo actual del producto */}
                        {!loadingHistorial && historialCostoActual > 0 && (
                            <div style={{
                                marginBottom: '16px',
                                padding: '10px 16px',
                                background: '#fefce8',
                                borderRadius: '8px',
                                border: '1px solid #fde68a',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <span style={{ fontSize: '14px', color: '#92400e' }}>
                                    Costo actual ({historialCostoTipoActual === 'costo_precios' ? 'Costo Precios' : 'Precio Compra'}):
                                </span>
                                <span style={{ fontSize: '18px', fontWeight: 700, color: '#b45309' }}>
                                    {formatMoney(historialCostoActual)}
                                </span>
                            </div>
                        )}

                        {loadingHistorial ? (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                                Cargando historial...
                            </div>
                        ) : historialData.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                                <History size={48} style={{ marginBottom: '12px', opacity: 0.4 }} />
                                <p>No hay ventas previas de este producto a este cliente</p>
                            </div>
                        ) : (
                            <table style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                fontSize: '15px'
                            }}>
                                <thead>
                                    <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                                        <th style={{ padding: '10px 8px', textAlign: 'left', color: '#374151', fontWeight: 600 }}>Fecha</th>
                                        <th style={{ padding: '10px 8px', textAlign: 'center', color: '#374151', fontWeight: 600 }}>Cant.</th>
                                        <th style={{ padding: '10px 8px', textAlign: 'right', color: '#374151', fontWeight: 600 }}>Precio</th>
                                        <th style={{ padding: '10px 8px', textAlign: 'center', color: '#374151', fontWeight: 600 }}>Tipo</th>
                                        <th style={{ padding: '10px 8px', textAlign: 'right', color: '#374151', fontWeight: 600 }}>Costo</th>
                                        <th style={{ padding: '10px 8px', textAlign: 'right', color: '#374151', fontWeight: 600 }}>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {historialData.map((h, idx) => (
                                        <tr key={idx} style={{
                                            borderBottom: '1px solid #f3f4f6',
                                            background: idx % 2 === 0 ? 'white' : '#f9fafb'
                                        }}>
                                            <td style={{ padding: '10px 8px', color: '#374151' }}>
                                                <div>{new Date(h.fecha).toLocaleDateString('es-MX')}</div>
                                                <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                                                    {new Date(h.fecha).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                                                </div>
                                            </td>
                                            <td style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 600, color: '#1f2937' }}>
                                                {h.cantidad}
                                            </td>
                                            <td style={{ padding: '10px 8px', textAlign: 'right' }}>
                                                <button
                                                    onClick={() => aplicarPrecioHistorial(h.precio_unitario)}
                                                    title="Click para aplicar este precio"
                                                    style={{
                                                        background: 'none',
                                                        border: '1px dashed #059669',
                                                        borderRadius: '4px',
                                                        padding: '4px 10px',
                                                        cursor: 'pointer',
                                                        color: '#059669',
                                                        fontWeight: 700,
                                                        fontSize: '15px',
                                                        transition: 'all 0.15s'
                                                    }}
                                                    onMouseEnter={(e) => { e.target.style.background = '#ecfdf5'; e.target.style.borderStyle = 'solid'; }}
                                                    onMouseLeave={(e) => { e.target.style.background = 'none'; e.target.style.borderStyle = 'dashed'; }}
                                                >
                                                    {formatMoney(h.precio_unitario)}
                                                </button>
                                            </td>
                                            <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                                                <span style={{
                                                    fontSize: '12px',
                                                    fontWeight: 600,
                                                    padding: '2px 8px',
                                                    borderRadius: '4px',
                                                    color: 'white',
                                                    backgroundColor:
                                                        h.precio_tipo === 'precio_lista' ? '#0284c7' :
                                                        h.precio_tipo === 'precio1' ? '#1e40af' :
                                                        h.precio_tipo === 'precio2' ? '#059669' :
                                                        h.precio_tipo === 'precio3' ? '#b45309' :
                                                        h.precio_tipo === 'precio_libre' ? '#dc2626' : '#6b7280'
                                                }}>
                                                    {h.precio_tipo === 'precio_lista' ? 'PL' :
                                                     h.precio_tipo === 'precio1' ? 'P1' :
                                                     h.precio_tipo === 'precio2' ? 'P2' :
                                                     h.precio_tipo === 'precio3' ? 'P3' :
                                                     h.precio_tipo === 'precio4' ? 'P4' :
                                                     h.precio_tipo === 'precio5' ? 'P5' :
                                                     h.precio_tipo === 'precio6' ? 'P6' :
                                                     h.precio_tipo === 'precio_libre' ? 'LIBRE' :
                                                     h.precio_tipo || 'N/A'}
                                                </span>
                                            </td>
                                            <td style={{ padding: '10px 8px', textAlign: 'right' }}>
                                                {h.costo > 0 ? (
                                                    <div>
                                                        <div style={{ fontWeight: 600, color: '#b45309' }}>
                                                            {formatMoney(h.costo)}
                                                        </div>
                                                        {historialCostoActual > 0 && h.costo !== historialCostoActual && (
                                                            <div style={{
                                                                fontSize: '11px',
                                                                color: h.costo < historialCostoActual ? '#dc2626' : '#16a34a',
                                                                fontWeight: 600
                                                            }}>
                                                                {h.costo < historialCostoActual ? '▲' : '▼'} {formatMoney(Math.abs(historialCostoActual - h.costo))}
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span style={{ fontSize: '12px', color: '#9ca3af' }}>—</span>
                                                )}
                                            </td>
                                            <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 700, color: '#1f2937' }}>
                                                {formatMoney(h.total)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}

                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            marginTop: '20px',
                            paddingTop: '16px',
                            borderTop: '1px solid #e5e7eb'
                        }}>
                            <Button variant="secondary" onClick={() => setShowHistorialModal(false)}>
                                Cerrar
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default CounterSalesPageRefactored;
